import React, { useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PauseCircleRoundedIcon from '@mui/icons-material/PauseCircleRounded';
import PlayCircleRoundedIcon from '@mui/icons-material/PlayCircleRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

function IndustrialModuleGraphic({
  moduleIndex,
  color,
}: {
  moduleIndex: number;
  color: string;
}) {
  const canvasSx = {
    position: 'relative',
    width: '100%',
    maxWidth: 560,
    minHeight: {
      xs: 230,
      md: 270,
    },
    mx: 'auto',
    overflow: 'hidden',
    borderTop: `1px solid ${alpha(color, 0.22)}`,
    borderBottom: `1px solid ${alpha('#94a3b8', 0.12)}`,
    backgroundImage: `
      linear-gradient(${alpha('#94a3b8', 0.045)} 1px, transparent 1px),
      linear-gradient(90deg, ${alpha('#94a3b8', 0.045)} 1px, transparent 1px),
      radial-gradient(circle at 50% 50%, ${alpha(color, 0.055)} 0%, transparent 68%)
    `,
    backgroundSize: '44px 44px, 44px 44px, 100% 100%',
  };

  if (moduleIndex === 0) {
    const areas = [
      {
        label: 'Área 1',
        status: 'Actividad estable',
        value: 82,
        color: '#22c55e',
      },
      {
        label: 'Área 2',
        status: 'Preparada',
        value: 58,
        color: '#38bdf8',
      },
      {
        label: 'Área 3',
        status: 'Requiere atención',
        value: 34,
        color: '#f59e0b',
      },
    ];

    return (
      <Box sx={canvasSx}>
        <Stack
          spacing={2}
          sx={{
            position: 'relative',
            zIndex: 1,
            p: {
              xs: 2.2,
              md: 3,
            },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Typography
              sx={{
                color,
                fontSize: '0.66rem',
                fontWeight: 950,
                letterSpacing: 1.4,
              }}
            >
              VISIÓN OPERATIVA
            </Typography>

            <Typography
              sx={{
                color: '#64748b',
                fontSize: '0.66rem',
                fontWeight: 850,
              }}
            >
              ESTADO GENERAL
            </Typography>
          </Stack>

          <Stack spacing={1.5}>
            {areas.map((area) => (
              <Box
                key={area.label}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '110px minmax(140px, 1fr) minmax(120px, 0.85fr)',
                  },
                  gap: {
                    xs: 0.8,
                    sm: 1.6,
                  },
                  alignItems: 'center',
                  py: 1.1,
                  borderBottom: `1px solid ${alpha('#94a3b8', 0.08)}`,
                  '&:last-of-type': {
                    borderBottom: 'none',
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.9}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: area.color,
                      boxShadow: `0 0 12px ${alpha(area.color, 0.65)}`,
                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontSize: '0.8rem',
                      fontWeight: 900,
                    }}
                  >
                    {area.label}
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    color: area.color,
                    fontSize: '0.76rem',
                    fontWeight: 900,
                  }}
                >
                  {area.status}
                </Typography>

                <Box>
                  <Box
                    sx={{
                      height: 6,
                      overflow: 'hidden',
                      borderRadius: 999,
                      bgcolor: alpha('#94a3b8', 0.12),
                    }}
                  >
                    <Box
                      sx={{
                        width: `${area.value}%`,
                        height: '100%',
                        borderRadius: 999,
                        bgcolor: area.color,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Box>
    );
  }

  if (moduleIndex === 1) {
    const layers = [
      {
        label: 'Materiales',
        value: 58,
        color: '#22c55e',
      },
      {
        label: 'Mano de obra',
        value: 24,
        color: '#fcd34d',
      },
      {
        label: 'Indirectos',
        value: 18,
        color: '#38bdf8',
      },
    ];

    return (
      <Box sx={canvasSx}>
        <Stack
          spacing={2.2}
          sx={{
            position: 'relative',
            zIndex: 1,
            p: {
              xs: 2.2,
              md: 3,
            },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Typography
              sx={{
                color,
                fontSize: '0.66rem',
                fontWeight: 950,
                letterSpacing: 1.4,
              }}
            >
              ESTRUCTURA DEL COSTE
            </Typography>

            <Typography
              sx={{
                color: '#64748b',
                fontSize: '0.66rem',
                fontWeight: 850,
              }}
            >
              DISTRIBUCIÓN RELATIVA
            </Typography>
          </Stack>

          <Box
            sx={{
              display: 'flex',
              width: '100%',
              height: 34,
              overflow: 'hidden',
              borderRadius: 1.2,
              border: `1px solid ${alpha('#94a3b8', 0.12)}`,
            }}
          >
            {layers.map((layer) => (
              <Box
                key={layer.label}
                sx={{
                  width: `${layer.value}%`,
                  height: '100%',
                  bgcolor: alpha(layer.color, 0.72),
                  borderRight: `1px solid ${alpha('#081120', 0.7)}`,
                  '&:last-of-type': {
                    borderRight: 'none',
                  },
                }}
              />
            ))}
          </Box>

          <Stack spacing={1.4}>
            {layers.map((layer) => (
              <Box key={layer.label}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Stack
                    direction="row"
                    spacing={0.9}
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: layer.color,
                      }}
                    />

                    <Typography
                      sx={{
                        color: '#cbd5e1',
                        fontSize: '0.78rem',
                        fontWeight: 850,
                      }}
                    >
                      {layer.label}
                    </Typography>
                  </Stack>

                  <Typography
                    sx={{
                      color: layer.color,
                      fontSize: '0.78rem',
                      fontWeight: 900,
                    }}
                  >
                    {layer.value} %
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    mt: 0.6,
                    height: 5,
                    overflow: 'hidden',
                    borderRadius: 999,
                    bgcolor: alpha('#94a3b8', 0.1),
                  }}
                >
                  <Box
                    sx={{
                      width: `${layer.value}%`,
                      height: '100%',
                      borderRadius: 999,
                      bgcolor: layer.color,
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Box>
    );
  }

  if (moduleIndex === 2) {
    const warehouseCells = [
      'rack', 'rack', 'aisle', 'floor', 'floor', 'blocked', 'empty', 'empty',
      'rack', 'rack', 'aisle', 'floor', 'empty', 'blocked', 'rack', 'rack',
      'empty', 'empty', 'aisle', 'rack', 'rack', 'rack', 'floor', 'floor',
      'floor', 'floor', 'aisle', 'rack', 'empty', 'empty', 'blocked', 'empty',
      'blocked', 'empty', 'aisle', 'rack', 'rack', 'floor', 'floor', 'empty',
    ];

    const getCellTone = (type: string) => {
      if (type === 'aisle') {
        return {
          bgcolor: alpha('#22c55e', 0.58),
          borderColor: alpha('#86efac', 0.62),
        };
      }

      if (type === 'rack') {
        return {
          bgcolor: alpha('#ef4444', 0.68),
          borderColor: alpha('#fecaca', 0.64),
        };
      }

      if (type === 'floor') {
        return {
          bgcolor: alpha('#f59e0b', 0.62),
          borderColor: alpha('#fde68a', 0.66),
        };
      }

      if (type === 'blocked') {
        return {
          bgcolor: alpha('#64748b', 0.65),
          borderColor: alpha('#94a3b8', 0.58),
        };
      }

      return {
        bgcolor: alpha('#ffffff', 0.018),
        borderColor: alpha('#94a3b8', 0.09),
      };
    };

    return (
      <Box sx={canvasSx}>
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'minmax(0, 1fr) 130px',
            },
            gap: 2.5,
            alignItems: 'center',
            p: {
              xs: 2.2,
              md: 3,
            },
          }}
        >
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Typography
                sx={{
                  color,
                  fontSize: '0.66rem',
                  fontWeight: 950,
                  letterSpacing: 1.4,
                }}
              >
                DISTRIBUCIÓN DEL ESPACIO
              </Typography>

              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.66rem',
                  fontWeight: 850,
                }}
              >
                VISTA GENERAL
              </Typography>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
                gap: 0.55,
              }}
            >
              {warehouseCells.map((type, index) => (
                <Box
                  key={`${type}-${index}`}
                  sx={{
                    aspectRatio: '1 / 1',
                    minWidth: 0,
                    borderRadius: 0.65,
                    border: '1px solid',
                    ...getCellTone(type),
                  }}
                />
              ))}
            </Box>
          </Box>

          <Stack spacing={1.25}>
            {[
              {
                label: 'Estantería',
                color: '#ef4444',
              },
              {
                label: 'Pasillo',
                color: '#22c55e',
              },
              {
                label: 'Suelo',
                color: '#f59e0b',
              },
              {
                label: 'Bloqueado',
                color: '#64748b',
              },
            ].map((item) => (
              <Stack
                key={item.label}
                direction="row"
                spacing={0.9}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 0.5,
                    bgcolor: alpha(item.color, 0.72),
                    border: `1px solid ${alpha(item.color, 0.9)}`,
                    flexShrink: 0,
                  }}
                />

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.74rem',
                    fontWeight: 800,
                  }}
                >
                  {item.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>
    );
  }

  const stages = [
    {
      label: 'Entrada',
      left: 4,
      width: 25,
      color: '#22c55e',
    },
    {
      label: 'Preparación',
      left: 22,
      width: 34,
      color: '#38bdf8',
    },
    {
      label: 'Ejecución',
      left: 50,
      width: 33,
      color: '#a78bfa',
    },
    {
      label: 'Cierre',
      left: 80,
      width: 16,
      color: '#fcd34d',
    },
  ];

  return (
    <Box sx={canvasSx}>
      <Stack
        spacing={2}
        sx={{
          position: 'relative',
          zIndex: 1,
          p: {
            xs: 2.2,
            md: 3,
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Typography
            sx={{
              color,
              fontSize: '0.66rem',
              fontWeight: 950,
              letterSpacing: 1.4,
            }}
          >
            RECORRIDO POR ETAPAS
          </Typography>

          <Typography
            sx={{
              color: '#64748b',
              fontSize: '0.66rem',
              fontWeight: 850,
            }}
          >
            PLAN Y PREVISIÓN
          </Typography>
        </Stack>

        <Box
          sx={{
            position: 'relative',
            pt: 1.5,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: {
                xs: 76,
                sm: 112,
              },
              right: 0,
              backgroundImage: `linear-gradient(90deg, ${alpha('#94a3b8', 0.08)} 1px, transparent 1px)`,
              backgroundSize: '20% 100%',
              pointerEvents: 'none',
            }}
          />

          <Stack spacing={1.55}>
            {stages.map((stage) => (
              <Box
                key={stage.label}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '68px minmax(0, 1fr)',
                    sm: '104px minmax(0, 1fr)',
                  },
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    color: '#cbd5e1',
                    fontSize: '0.74rem',
                    fontWeight: 850,
                  }}
                >
                  {stage.label}
                </Typography>

                <Box
                  sx={{
                    position: 'relative',
                    height: 18,
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${stage.left}%`,
                      width: `${stage.width}%`,
                      top: 3,
                      height: 12,
                      borderRadius: 999,
                      bgcolor: alpha(stage.color, 0.68),
                      border: `1px solid ${alpha(stage.color, 0.84)}`,
                      boxShadow: `0 0 14px ${alpha(stage.color, 0.14)}`,
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          sx={{
            pl: {
              xs: '76px',
              sm: '112px',
            },
            pt: 0.5,
          }}
        >
          {['Inicio', 'Hoy', 'Objetivo'].map((label) => (
            <Typography
              key={label}
              sx={{
                color: '#64748b',
                fontSize: '0.66rem',
                fontWeight: 800,
              }}
            >
              {label}
            </Typography>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}

type FactoryControlVisualProps = {
  activeStep: number;
  compact?: boolean;
};

function FactoryControlVisual({
  activeStep,
  compact = false,
}: FactoryControlVisualProps) {
  const tabs = [
    'Estado actual',
    'Tiempos',
    'Atención',
    'Rendimiento',
  ];

  const renderCurrentState = () => {
    const lineCards = [
      {
        line: 'Línea A',
        tone: 'running',
        chip: 'Produciendo',
        title: 'Orden activa',
        subtitle: 'Referencia estándar A · lote en curso',
        stats: [
          ['Inicio', '08:10'],
          ['Fin previsto', '12:35'],
          ['Ritmo', 'Estable'],
          ['Cobertura', 'Completa'],
        ],
        footerLabel: 'Seguimiento',
        footerValue: 'Sin incidencias',
        progress: 76,
      },
      {
        line: 'Línea B',
        tone: 'running',
        chip: 'Produciendo',
        title: 'Fabricación en marcha',
        subtitle: 'Referencia estándar B · ciclo actual',
        stats: [
          ['Inicio', '09:05'],
          ['Fin previsto', '13:10'],
          ['Ritmo', 'Correcto'],
          ['Cobertura', 'Actualizada'],
        ],
        footerLabel: 'Estado',
        footerValue: 'Dentro de previsión',
        progress: 64,
      },
      {
        line: 'Línea C',
        tone: 'delayed',
        chip: 'Con retraso',
        title: 'Orden en curso',
        subtitle: 'Referencia estándar C · desviación temporal',
        stats: [
          ['Inicio', '09:25'],
          ['Fin previsto', '12:10'],
          ['Desviación', '+18 min'],
          ['Acción', 'Seguimiento'],
        ],
        footerLabel: 'Atención',
        footerValue: 'Revisión recomendada',
        progress: 88,
      },
      {
        line: 'Línea D',
        tone: 'stopped',
        chip: 'Parada',
        title: 'Incidencia abierta',
        subtitle: 'Parada temporal en espera de intervención',
        stats: [
          ['Desde', '10:42'],
          ['Parada', '27 min'],
          ['Impacto', 'Turno actual'],
          ['Aviso', 'Emitido'],
        ],
        footerLabel: 'Prioridad',
        footerValue: 'Atención inmediata',
        progress: 34,
      },
      {
        line: 'Línea E',
        tone: 'ready',
        chip: 'Preparada',
        title: 'Lista para siguiente orden',
        subtitle: 'Cambio completado y línea disponible',
        stats: [
          ['Cierre previo', '11:58'],
          ['Siguiente lote', 'Asignado'],
          ['Disponibilidad', 'Inmediata'],
          ['Estado', 'Preparada'],
        ],
        footerLabel: 'Disponibilidad',
        footerValue: 'Lista para iniciar',
        progress: 100,
      },
      {
        line: 'Línea F',
        tone: 'idle',
        chip: 'Sin actividad',
        title: 'Sin orden activa',
        subtitle: 'Línea disponible en este momento',
        stats: [
          ['Última actividad', '08:50'],
          ['Estado', 'Disponible'],
          ['Calidad', 'Conforme'],
          ['Ocupación', 'Baja'],
        ],
        footerLabel: 'Planificación',
        footerValue: 'Pendiente de asignación',
        progress: 18,
      },
    ] as const;

    const toneMap = {
      running: {
        color: '#22c55e',
        softBg: 'rgba(20,83,45,0.18)',
        border: alpha('#22c55e', 0.28),
        text: '#86efac',
        bar: '#22c55e',
        icon: PlayCircleRoundedIcon,
      },
      delayed: {
        color: '#f59e0b',
        softBg: 'rgba(120,53,15,0.16)',
        border: alpha('#f59e0b', 0.28),
        text: '#fcd34d',
        bar: '#f59e0b',
        icon: TimelineRoundedIcon,
      },
      stopped: {
        color: '#ef4444',
        softBg: 'rgba(127,29,29,0.18)',
        border: alpha('#ef4444', 0.28),
        text: '#fca5a5',
        bar: '#ef4444',
        icon: PauseCircleRoundedIcon,
      },
      ready: {
        color: '#38bdf8',
        softBg: 'rgba(8,47,73,0.18)',
        border: alpha('#38bdf8', 0.24),
        text: '#93c5fd',
        bar: '#38bdf8',
        icon: CheckCircleRoundedIcon,
      },
      idle: {
        color: '#94a3b8',
        softBg: 'rgba(15,23,42,0.42)',
        border: alpha('#94a3b8', 0.18),
        text: '#cbd5e1',
        bar: '#94a3b8',
        icon: PrecisionManufacturingRoundedIcon,
      },
    } as const;

    return (
      <Stack
        spacing={1.8}
        sx={{
          height: '100%',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.38 }}
        >
          <Box
            sx={{
              px: 1.25,
              py: 1.1,
              borderRadius: 2.5,
              border: `1px solid ${alpha('#94a3b8', 0.16)}`,
              bgcolor: alpha('#0f172a', 0.42),
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'minmax(180px, 0.95fr) minmax(0, 1.8fr)',
                },
                gap: 1.2,
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: '#22c55e',
                    fontSize: '0.66rem',
                    fontWeight: 950,
                    letterSpacing: 1.2,
                  }}
                >
                  LECTURA ACTUAL
                </Typography>

                <Typography
                  sx={{
                    mt: 0.35,
                    color: '#e2e8f0',
                    fontSize: '0.76rem',
                    fontWeight: 850,
                  }}
                >
                  Visión por líneas y estados
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={0.8}
                flexWrap="wrap"
                useFlexGap
                justifyContent={{
                  xs: 'flex-start',
                  md: 'flex-end',
                }}
              >
                {[
                  '6 líneas visibles',
                  '2 produciendo',
                  '1 con retraso',
                  '1 parada',
                  '2 disponibles',
                ].map((item, index) => (
                  <Box
                    key={item}
                    component={motion.div}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.35,
                      delay: 0.05 + index * 0.05,
                    }}
                    sx={{
                      px: 0.9,
                      py: 0.45,
                      borderRadius: 999,
                      bgcolor: alpha('#ffffff', 0.04),
                      border: `1px solid ${alpha('#94a3b8', 0.14)}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#cbd5e1',
                        fontSize: '0.67rem',
                        fontWeight: 900,
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        </motion.div>

        <Box
          sx={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1.45,
            alignContent: 'start',
          }}
        >
          {lineCards.map((item, index) => {
            const tone = toneMap[item.tone];
            const LineIcon = tone.icon;

            return (
              <motion.div
                key={item.line}
                initial={{
                  opacity: 0,
                  y: 16,
                  scale: 0.985,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                }}
                transition={{
                  duration: 0.42,
                  delay: 0.06 + index * 0.05,
                  ease: 'easeOut',
                }}
                style={{ height: '100%' }}
              >
                <Box
                  sx={{
                    height: '100%',
                    minHeight: 236,
                    p: 1.45,
                    borderRadius: 2.8,
                    bgcolor: tone.softBg,
                    border: `1px solid ${tone.border}`,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Stack direction="row" spacing={0.85} alignItems="center">
                      <LineIcon
                        sx={{
                          color: tone.color,
                          fontSize: 18,
                        }}
                      />

                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontSize: '0.84rem',
                          fontWeight: 950,
                        }}
                      >
                        {item.line}
                      </Typography>
                    </Stack>

                    <Box
                      sx={{
                        px: 0.85,
                        py: 0.38,
                        borderRadius: 999,
                        bgcolor: alpha(tone.color, 0.14),
                        border: `1px solid ${alpha(tone.color, 0.18)}`,
                      }}
                    >
                      <Typography
                        sx={{
                          color: tone.text,
                          fontSize: '0.66rem',
                          fontWeight: 900,
                        }}
                      >
                        {item.chip}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      mt: 1.2,
                      p: 1.15,
                      borderRadius: 2.2,
                      bgcolor: alpha('#0f172a', 0.22),
                      border: `1px solid ${alpha(tone.color, 0.16)}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontSize: '0.88rem',
                        fontWeight: 950,
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.4,
                        color: '#94a3b8',
                        fontSize: '0.72rem',
                        lineHeight: 1.45,
                      }}
                    >
                      {item.subtitle}
                    </Typography>

                    <Box
                      sx={{
                        mt: 1.15,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 0.95,
                      }}
                    >
                      {item.stats.map(([label, value]) => (
                        <Box key={`${item.line}-${label}`}>
                          <Typography
                            sx={{
                              color: '#64748b',
                              fontSize: '0.61rem',
                              fontWeight: 900,
                              textTransform: 'uppercase',
                            }}
                          >
                            {label}
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.18,
                              color: '#f8fafc',
                              fontSize: '0.82rem',
                              fontWeight: 900,
                              lineHeight: 1.15,
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      mt: 'auto',
                      pt: 1.05,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                      alignItems="center"
                    >
                      <Typography
                        sx={{
                          color: tone.text,
                          fontSize: '0.69rem',
                          fontWeight: 900,
                        }}
                      >
                        {item.footerLabel}
                      </Typography>

                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontSize: '0.76rem',
                          fontWeight: 900,
                          textAlign: 'right',
                        }}
                      >
                        {item.footerValue}
                      </Typography>
                    </Stack>

                    <Box
                      sx={{
                        mt: 0.8,
                        height: 5,
                        overflow: 'hidden',
                        borderRadius: 999,
                        bgcolor: alpha('#94a3b8', 0.12),
                      }}
                    >
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${item.progress}%` }}
                        transition={{
                          duration: 0.85,
                          delay: 0.2 + index * 0.05,
                        }}
                        style={{
                          height: '100%',
                          borderRadius: 999,
                          backgroundColor: tone.bar,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            );
          })}
        </Box>
      </Stack>
    );
  };

  const renderTiming = () => {
    const timelineRows = [
      {
        line: 'Línea A',
        status: 'Dentro de previsión',
        statusColor: '#86efac',
        segments: [
          {
            label: 'Preparación',
            left: 0,
            width: 12,
            color: '#a78bfa',
          },
          {
            label: 'Orden A',
            left: 12,
            width: 42,
            color: '#22c55e',
          },
          {
            label: 'Cambio',
            left: 54,
            width: 10,
            color: '#a78bfa',
          },
          {
            label: 'Orden B',
            left: 64,
            width: 30,
            color: '#22c55e',
          },
        ],
      },
      {
        line: 'Línea B',
        status: 'Parada recuperada',
        statusColor: '#fca5a5',
        segments: [
          {
            label: 'Preparación',
            left: 0,
            width: 10,
            color: '#a78bfa',
          },
          {
            label: 'Orden C',
            left: 10,
            width: 34,
            color: '#22c55e',
          },
          {
            label: 'Parada',
            left: 44,
            width: 12,
            color: '#ef4444',
          },
          {
            label: 'Orden D',
            left: 56,
            width: 35,
            color: '#22c55e',
          },
        ],
      },
      {
        line: 'Línea C',
        status: 'Desviación moderada',
        statusColor: '#fcd34d',
        segments: [
          {
            label: 'Espera',
            left: 0,
            width: 13,
            color: '#64748b',
          },
          {
            label: 'Preparación',
            left: 13,
            width: 11,
            color: '#a78bfa',
          },
          {
            label: 'Orden E',
            left: 24,
            width: 47,
            color: '#22c55e',
          },
          {
            label: 'Tiempo extra',
            left: 71,
            width: 18,
            color: '#f59e0b',
          },
        ],
      },
      {
        line: 'Línea D',
        status: 'Actividad estable',
        statusColor: '#93c5fd',
        segments: [
          {
            label: 'Preparación',
            left: 0,
            width: 14,
            color: '#a78bfa',
          },
          {
            label: 'Orden F',
            left: 14,
            width: 28,
            color: '#22c55e',
          },
          {
            label: 'Ajuste',
            left: 42,
            width: 9,
            color: '#ef4444',
          },
          {
            label: 'Orden G',
            left: 51,
            width: 41,
            color: '#22c55e',
          },
        ],
      },
    ];

    const periodMetrics = [
      {
        label: 'Fabricación',
        value: '21 h 40 min',
        detail: 'Frente a 24 h previstas',
        progress: 90,
        color: '#22c55e',
      },
      {
        label: 'Preparación',
        value: '1 h 35 min',
        detail: 'Cuatro cambios registrados',
        progress: 42,
        color: '#a78bfa',
      },
      {
        label: 'Paradas',
        value: '48 min',
        detail: 'Dos incidencias recuperadas',
        progress: 24,
        color: '#ef4444',
      },
      {
        label: 'Tiempo ocupado',
        value: '91 %',
        detail: 'Cobertura temporal completa',
        progress: 91,
        color: '#38bdf8',
      },
    ];

    return (
      <Stack
        spacing={1.65}
        sx={{
          minHeight: compact
            ? 500
            : {
                lg: 'clamp(500px, calc(100vh - 250px), 620px)',
              },
        }}
      >
        <motion.div
          initial={{
            opacity: 0,
            y: 14,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.4,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'minmax(190px, 0.85fr) minmax(0, 1.8fr)',
              },
              gap: 1.4,
              alignItems: 'center',
              px: 1.4,
              py: 1.2,
              borderRadius: 2.7,
              bgcolor: alpha('#0f172a', 0.42),
              border: `1px solid ${alpha('#94a3b8', 0.16)}`,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <TimelineRoundedIcon
                sx={{
                  color: '#38bdf8',
                  fontSize: 22,
                }}
              />

              <Box>
                <Typography
                  sx={{
                    color: '#38bdf8',
                    fontSize: '0.65rem',
                    fontWeight: 950,
                    letterSpacing: 1.15,
                  }}
                >
                  TURNO ACTUAL
                </Typography>

                <Typography
                  sx={{
                    mt: 0.25,
                    color: '#f8fafc',
                    fontSize: '0.82rem',
                    fontWeight: 950,
                  }}
                >
                  06:00 — 14:00
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={0.8}
              flexWrap="wrap"
              useFlexGap
              justifyContent={{
                xs: 'flex-start',
                md: 'flex-end',
              }}
            >
              {[
                '4 líneas',
                '7 órdenes',
                '4 preparaciones',
                '2 paradas',
              ].map((item, index) => (
                <motion.div
                  key={item}
                  initial={{
                    opacity: 0,
                    y: 8,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.35,
                    delay: 0.05 + index * 0.05,
                  }}
                >
                  <Box
                    sx={{
                      px: 0.9,
                      py: 0.45,
                      borderRadius: 999,
                      bgcolor: alpha('#ffffff', 0.04),
                      border: `1px solid ${alpha('#94a3b8', 0.14)}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#cbd5e1',
                        fontSize: '0.67rem',
                        fontWeight: 900,
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Stack>
          </Box>
        </motion.div>

        <Box
          sx={{
            flex: 1,
            minHeight: {
              xs: 290,
              lg: 330,
            },
            p: {
              xs: 1.35,
              md: 1.7,
            },
            borderRadius: 3,
            bgcolor: alpha('#0f172a', 0.34),
            border: `1px solid ${alpha('#94a3b8', 0.14)}`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'flex-start',
              sm: 'center',
            }}
            spacing={0.8}
          >
            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontSize: '0.88rem',
                  fontWeight: 950,
                }}
              >
                Actividad por línea
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,
                  color: '#64748b',
                  fontSize: '0.66rem',
                }}
              >
                Fabricación, preparación, espera y parada sobre una escala común
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1.4}
              flexWrap="wrap"
              useFlexGap
            >
              {[
                ['Preparación', '#a78bfa'],
                ['Fabricación', '#22c55e'],
                ['Parada', '#ef4444'],
                ['Espera', '#64748b'],
                ['Desviación', '#f59e0b'],
              ].map(([label, color]) => (
                <Stack
                  key={label}
                  direction="row"
                  spacing={0.55}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 4,
                      borderRadius: 999,
                      bgcolor: color,
                    }}
                  />

                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.61rem',
                      fontWeight: 800,
                    }}
                  >
                    {label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>

          <Box
            sx={{
              mt: 1.6,
              display: 'grid',
              gridTemplateColumns: {
                xs: '72px minmax(0, 1fr)',
                md: '96px minmax(0, 1fr)',
              },
              gap: 1.2,
              alignItems: 'end',
            }}
          >
            <Box />

            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{
                px: 0.2,
              }}
            >
              {['06:00', '08:00', '10:00', '12:00', '14:00'].map(
                (time) => (
                  <Typography
                    key={time}
                    sx={{
                      color: '#64748b',
                      fontSize: '0.59rem',
                      fontWeight: 800,
                    }}
                  >
                    {time}
                  </Typography>
                ),
              )}
            </Stack>
          </Box>

          <Stack
            spacing={1.35}
            sx={{
              mt: 0.85,
              flex: 1,
              justifyContent: 'space-evenly',
            }}
          >
            {timelineRows.map((row, rowIndex) => (
              <motion.div
                key={row.line}
                initial={{
                  opacity: 0,
                  x: 16,
                }}
                animate={{
                  opacity: 1,
                  x: 0,
                }}
                transition={{
                  duration: 0.4,
                  delay: rowIndex * 0.08,
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '72px minmax(0, 1fr)',
                      md: '96px minmax(0, 1fr)',
                    },
                    gap: 1.2,
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontSize: '0.73rem',
                        fontWeight: 950,
                      }}
                    >
                      {row.line}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.22,
                        color: row.statusColor,
                        fontSize: '0.58rem',
                        fontWeight: 850,
                        lineHeight: 1.25,
                      }}
                    >
                      {row.status}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      position: 'relative',
                      height: 38,
                      overflow: 'hidden',
                      borderRadius: 1.8,
                      bgcolor: alpha('#020617', 0.3),
                      border: `1px solid ${alpha('#94a3b8', 0.1)}`,
                      backgroundImage: `linear-gradient(90deg, ${alpha('#94a3b8', 0.08)} 1px, transparent 1px)`,
                      backgroundSize: '25% 100%',
                    }}
                  >
                    {row.segments.map((segment, segmentIndex) => (
                      <motion.div
                        key={`${row.line}-${segment.label}`}
                        initial={{
                          width: 0,
                          opacity: 0,
                        }}
                        animate={{
                          width: `${segment.width}%`,
                          opacity: 1,
                        }}
                        transition={{
                          duration: 0.72,
                          delay:
                            0.12 +
                            rowIndex * 0.1 +
                            segmentIndex * 0.08,
                          ease: 'easeOut',
                        }}
                        style={{
                          position: 'absolute',
                          left: `${segment.left}%`,
                          top: 7,
                          height: 22,
                          overflow: 'hidden',
                          borderRadius: 6,
                          backgroundColor: segment.color,
                          boxShadow: `0 0 14px ${alpha(segment.color, 0.16)}`,
                        }}
                      >
                        {segment.width >= 11 ? (
                          <Typography
                            sx={{
                              display: {
                                xs: 'none',
                                sm: 'block',
                              },
                              px: 0.7,
                              color: '#f8fafc',
                              fontSize: '0.57rem',
                              fontWeight: 900,
                              lineHeight: '22px',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                            }}
                          >
                            {segment.label}
                          </Typography>
                        ) : null}
                      </motion.div>
                    ))}

                    <motion.div
                      initial={{
                        scaleY: 0,
                        opacity: 0,
                      }}
                      animate={{
                        scaleY: 1,
                        opacity: 1,
                      }}
                      transition={{
                        duration: 0.5,
                        delay: 0.75 + rowIndex * 0.06,
                      }}
                      style={{
                        position: 'absolute',
                        left: '78%',
                        top: 3,
                        bottom: 3,
                        width: '1px',
                        backgroundColor: '#f8fafc',
                        boxShadow: `0 0 9px ${alpha('#f8fafc', 0.5)}`,
                        transformOrigin: 'top',
                      }}
                    />
                  </Box>
                </Box>
              </motion.div>
            ))}
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '72px minmax(0, 1fr)',
                md: '96px minmax(0, 1fr)',
              },
              gap: 1.2,
              mt: 0.8,
            }}
          >
            <Box />

            <Typography
              sx={{
                width: '78%',
                color: '#cbd5e1',
                fontSize: '0.58rem',
                fontWeight: 900,
                textAlign: 'right',
                transform: 'translateX(50%)',
              }}
            >
              AHORA
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1.15,
          }}
        >
          {periodMetrics.map((metric, index) => (
            <motion.div
              key={metric.label}
              initial={{
                opacity: 0,
                y: 14,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.4,
                delay: 0.48 + index * 0.07,
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  px: 1.25,
                  py: 1.1,
                  borderRadius: 2.5,
                  bgcolor: alpha('#0f172a', 0.38),
                  border: `1px solid ${alpha(metric.color, 0.22)}`,
                }}
              >
                <Typography
                  sx={{
                    color: metric.color,
                    fontSize: '0.62rem',
                    fontWeight: 950,
                    letterSpacing: 0.85,
                    textTransform: 'uppercase',
                  }}
                >
                  {metric.label}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.45,
                    color: '#f8fafc',
                    fontSize: {
                      xs: '1rem',
                      md: '1.15rem',
                    },
                    fontWeight: 950,
                    lineHeight: 1.05,
                  }}
                >
                  {metric.value}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.38,
                    color: '#64748b',
                    fontSize: '0.62rem',
                    lineHeight: 1.35,
                  }}
                >
                  {metric.detail}
                </Typography>

                <Box
                  sx={{
                    mt: 0.8,
                    height: 4,
                    overflow: 'hidden',
                    borderRadius: 999,
                    bgcolor: alpha('#94a3b8', 0.1),
                  }}
                >
                  <motion.div
                    initial={{
                      width: 0,
                    }}
                    animate={{
                      width: `${metric.progress}%`,
                    }}
                    transition={{
                      duration: 0.8,
                      delay: 0.65 + index * 0.07,
                    }}
                    style={{
                      height: '100%',
                      borderRadius: 999,
                      backgroundColor: metric.color,
                    }}
                  />
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>
      </Stack>
    );
  };

  const renderAttention = () => {
    const reviewSignals = [
      {
        label: 'Incidencia detectada',
        detail: 'Parada registrada en la actividad actual',
        status: 'Activa',
        color: '#ef4444',
        icon: PauseCircleRoundedIcon,
      },
      {
        label: 'Revisión operativa',
        detail: 'Responsable asignado y análisis iniciado',
        status: 'En curso',
        color: '#f59e0b',
        icon: TimelineRoundedIcon,
      },
      {
        label: 'Validación del parte',
        detail: 'Información pendiente de consolidación',
        status: 'Pendiente',
        color: '#a78bfa',
        icon: CheckCircleRoundedIcon,
      },
      {
        label: 'Control de calidad',
        detail: 'Una lectura requiere comprobación',
        status: 'Revisar',
        color: '#38bdf8',
        icon: PrecisionManufacturingRoundedIcon,
      },
    ];

    const stopCauses = [
      {
        label: 'Ajuste de máquina',
        incidents: '4 incidencias',
        duration: '1 h 42 min',
        progress: 86,
      },
      {
        label: 'Cambio de material',
        incidents: '3 incidencias',
        duration: '58 min',
        progress: 58,
      },
      {
        label: 'Mantenimiento',
        incidents: '2 incidencias',
        duration: '41 min',
        progress: 42,
      },
      {
        label: 'Control de calidad',
        incidents: '2 incidencias',
        duration: '26 min',
        progress: 28,
      },
      {
        label: 'Espera de recursos',
        incidents: '1 incidencia',
        duration: '17 min',
        progress: 18,
      },
    ];

    return (
      <Stack
        spacing={1.6}
        sx={{
          minHeight: compact
            ? 500
            : {
                lg: 'clamp(500px, calc(100vh - 250px), 620px)',
              },
        }}
      >
        <motion.div
          initial={{
            opacity: 0,
            y: 14,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.4,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'minmax(210px, 0.9fr) minmax(0, 1.7fr)',
              },
              gap: 1.4,
              alignItems: 'center',
              px: 1.4,
              py: 1.15,
              borderRadius: 2.7,
              bgcolor: alpha('#0f172a', 0.42),
              border: `1px solid ${alpha('#94a3b8', 0.16)}`,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <PauseCircleRoundedIcon
                sx={{
                  color: '#ef4444',
                  fontSize: 22,
                }}
              />

              <Box>
                <Typography
                  sx={{
                    color: '#ef4444',
                    fontSize: '0.65rem',
                    fontWeight: 950,
                    letterSpacing: 1.15,
                  }}
                >
                  ATENCIÓN OPERATIVA
                </Typography>

                <Typography
                  sx={{
                    mt: 0.25,
                    color: '#f8fafc',
                    fontSize: '0.82rem',
                    fontWeight: 950,
                  }}
                >
                  Señales conectadas con la actividad
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={0.8}
              flexWrap="wrap"
              useFlexGap
              justifyContent={{
                xs: 'flex-start',
                md: 'flex-end',
              }}
            >
              {[
                ['1 parada abierta', '#ef4444'],
                ['2 avisos', '#f59e0b'],
                ['1 validación', '#a78bfa'],
                ['1 revisión de calidad', '#38bdf8'],
              ].map(([label, color], index) => (
                <motion.div
                  key={label}
                  initial={{
                    opacity: 0,
                    y: 8,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.35,
                    delay: 0.05 + index * 0.05,
                  }}
                >
                  <Box
                    sx={{
                      px: 0.9,
                      py: 0.45,
                      borderRadius: 999,
                      bgcolor: alpha(String(color), 0.08),
                      border: `1px solid ${alpha(String(color), 0.2)}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color,
                        fontSize: '0.67rem',
                        fontWeight: 900,
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Stack>
          </Box>
        </motion.div>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 1.05fr) minmax(280px, 0.95fr)',
            },
            gap: 1.45,
          }}
        >
          <motion.div
            initial={{
              opacity: 0,
              x: -18,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            transition={{
              duration: 0.45,
              delay: 0.08,
            }}
          >
            <Box
              sx={{
                height: '100%',
                minHeight: 220,
                p: 1.7,
                borderRadius: 3,
                bgcolor: 'rgba(127,29,29,0.18)',
                border: `1px solid ${alpha('#ef4444', 0.32)}`,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -70,
                  right: -55,
                  width: 190,
                  height: 190,
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle, rgba(239,68,68,0.14) 0%, transparent 70%)',
                  pointerEvents: 'none',
                }}
              />

              <Stack
                direction="row"
                justifyContent="space-between"
                spacing={2}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      color: '#fca5a5',
                      fontSize: '0.65rem',
                      fontWeight: 950,
                      letterSpacing: 1.1,
                    }}
                  >
                    PARADA ABIERTA
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.55,
                      color: '#f8fafc',
                      fontSize: '1.08rem',
                      fontWeight: 950,
                    }}
                  >
                    Línea C
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.35,
                      color: '#fecaca',
                      fontSize: '0.73rem',
                    }}
                  >
                    Ajuste de máquina pendiente de intervención
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    bgcolor: alpha('#ef4444', 0.16),
                    border: `1px solid ${alpha('#ef4444', 0.34)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <PauseCircleRoundedIcon
                    sx={{
                      color: '#ef4444',
                      fontSize: 23,
                    }}
                  />
                </Box>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 1.1,
                  mt: 1.7,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {[
                  ['Desde', '10:42'],
                  ['Duración', '27 min'],
                  ['Impacto', 'Turno actual'],
                ].map(([label, value], index) => (
                  <motion.div
                    key={label}
                    initial={{
                      opacity: 0,
                      y: 10,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    transition={{
                      duration: 0.36,
                      delay: 0.16 + index * 0.07,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#94a3b8',
                        fontSize: '0.61rem',
                        fontWeight: 900,
                        textTransform: 'uppercase',
                      }}
                    >
                      {label}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.28,
                        color:
                          index === 1
                            ? '#fca5a5'
                            : '#f8fafc',
                        fontSize: {
                          xs: '0.88rem',
                          md: '1rem',
                        },
                        fontWeight: 950,
                        lineHeight: 1.1,
                      }}
                    >
                      {value}
                    </Typography>
                  </motion.div>
                ))}
              </Box>

              <Box
                sx={{
                  mt: 'auto',
                  pt: 1.45,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  spacing={1}
                  alignItems="center"
                >
                  <Typography
                    sx={{
                      color: '#fecaca',
                      fontSize: '0.69rem',
                      fontWeight: 900,
                    }}
                  >
                    Tiempo desde la detección
                  </Typography>

                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontSize: '0.75rem',
                      fontWeight: 950,
                    }}
                  >
                    Intervención en curso
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    mt: 0.8,
                    height: 6,
                    overflow: 'hidden',
                    borderRadius: 999,
                    bgcolor: alpha('#94a3b8', 0.12),
                  }}
                >
                  <motion.div
                    initial={{
                      width: 0,
                    }}
                    animate={{
                      width: '64%',
                    }}
                    transition={{
                      duration: 0.9,
                      delay: 0.28,
                    }}
                    style={{
                      height: '100%',
                      borderRadius: 999,
                      background:
                        'linear-gradient(90deg, #ef4444 0%, #f59e0b 100%)',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </motion.div>

          <Box
            sx={{
              minHeight: 220,
              p: 1.55,
              borderRadius: 3,
              bgcolor: alpha('#0f172a', 0.38),
              border: `1px solid ${alpha('#94a3b8', 0.15)}`,
            }}
          >
            <Typography
              sx={{
                color: '#f8fafc',
                fontSize: '0.86rem',
                fontWeight: 950,
              }}
            >
              Seguimiento conectado
            </Typography>

            <Typography
              sx={{
                mt: 0.25,
                color: '#64748b',
                fontSize: '0.65rem',
              }}
            >
              Estado de las señales asociadas a la actividad
            </Typography>

            <Stack
              spacing={0}
              sx={{
                mt: 1.2,
              }}
            >
              {reviewSignals.map((signal, index) => {
                const SignalIcon = signal.icon;

                return (
                  <motion.div
                    key={signal.label}
                    initial={{
                      opacity: 0,
                      x: 14,
                    }}
                    animate={{
                      opacity: 1,
                      x: 0,
                    }}
                    transition={{
                      duration: 0.38,
                      delay: 0.1 + index * 0.07,
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'grid',
                        gridTemplateColumns: '28px minmax(0, 1fr) auto',
                        gap: 0.85,
                        alignItems: 'center',
                        py: 0.85,
                        borderBottom:
                          index === reviewSignals.length - 1
                            ? 'none'
                            : `1px solid ${alpha('#94a3b8', 0.08)}`,
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          zIndex: 1,
                          width: 27,
                          height: 27,
                          borderRadius: '50%',
                          bgcolor: alpha(signal.color, 0.1),
                          border: `1px solid ${alpha(signal.color, 0.24)}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <SignalIcon
                          sx={{
                            color: signal.color,
                            fontSize: 17,
                          }}
                        />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: '#e2e8f0',
                            fontSize: '0.72rem',
                            fontWeight: 900,
                          }}
                        >
                          {signal.label}
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.16,
                            color: '#64748b',
                            fontSize: '0.61rem',
                            lineHeight: 1.35,
                          }}
                        >
                          {signal.detail}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          px: 0.75,
                          py: 0.35,
                          borderRadius: 999,
                          bgcolor: alpha(signal.color, 0.08),
                          border: `1px solid ${alpha(signal.color, 0.18)}`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: signal.color,
                            fontSize: '0.6rem',
                            fontWeight: 900,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {signal.status}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                );
              })}
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 220,
            p: {
              xs: 1.4,
              md: 1.65,
            },
            borderRadius: 3,
            bgcolor: alpha('#0f172a', 0.34),
            border: `1px solid ${alpha('#94a3b8', 0.14)}`,
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'flex-start',
              sm: 'center',
            }}
            spacing={0.8}
          >
            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontSize: '0.87rem',
                  fontWeight: 950,
                }}
              >
                Principales causas de parada
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,
                  color: '#64748b',
                  fontSize: '0.65rem',
                }}
              >
                Motivos ordenados por tiempo acumulado durante el periodo
              </Typography>
            </Box>

            <Box
              sx={{
                px: 0.85,
                py: 0.4,
                borderRadius: 999,
                bgcolor: alpha('#ef4444', 0.07),
                border: `1px solid ${alpha('#ef4444', 0.2)}`,
              }}
            >
              <Typography
                sx={{
                  color: '#fca5a5',
                  fontSize: '0.63rem',
                  fontWeight: 900,
                }}
              >
                3 h 04 min acumuladas
              </Typography>
            </Box>
          </Stack>

          <Stack
            spacing={0.8}
            sx={{
              mt: 1.25,
            }}
          >
            {stopCauses.map((cause, index) => (
              <motion.div
                key={cause.label}
                initial={{
                  opacity: 0,
                  y: 10,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  duration: 0.38,
                  delay: 0.24 + index * 0.06,
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'minmax(0, 1fr)',
                      sm: 'minmax(150px, 0.65fr) minmax(0, 1.5fr) 72px',
                    },
                    gap: {
                      xs: 0.5,
                      sm: 1.15,
                    },
                    alignItems: 'center',
                    py: 0.32,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: '#e2e8f0',
                        fontSize: '0.7rem',
                        fontWeight: 900,
                      }}
                    >
                      {cause.label}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.14,
                        color: '#64748b',
                        fontSize: '0.59rem',
                      }}
                    >
                      {cause.incidents}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      height: 6,
                      overflow: 'hidden',
                      borderRadius: 999,
                      bgcolor: alpha('#94a3b8', 0.1),
                    }}
                  >
                    <motion.div
                      initial={{
                        width: 0,
                      }}
                      animate={{
                        width: `${cause.progress}%`,
                      }}
                      transition={{
                        duration: 0.8,
                        delay: 0.34 + index * 0.06,
                        ease: 'easeOut',
                      }}
                      style={{
                        height: '100%',
                        borderRadius: 999,
                        background:
                          index === 0
                            ? 'linear-gradient(90deg, #ef4444 0%, #f97316 100%)'
                            : '#fb7185',
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      color:
                        index === 0
                          ? '#fca5a5'
                          : '#e2e8f0',
                      fontSize: '0.68rem',
                      fontWeight: 950,
                      textAlign: {
                        xs: 'left',
                        sm: 'right',
                      },
                    }}
                  >
                    {cause.duration}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Stack>
        </Box>
      </Stack>
    );
  };

  const renderPerformance = () => {
    const performanceLines = [
      {
        line: 'Línea A',
        activity: '8 órdenes · 11 tramos',
        expected: 74,
        actual: 78,
        realTime: '31 h 10 min',
        expectedTime: '29 h 45 min',
        deviation: '+4,8 %',
        preparation: '8 min',
        operating: '98 %',
        stops: '24 min',
        status: 'Dentro de tolerancia',
        color: '#22c55e',
      },
      {
        line: 'Línea B',
        activity: '6 órdenes · 9 tramos',
        expected: 68,
        actual: 86,
        realTime: '28 h 35 min',
        expectedTime: '23 h 50 min',
        deviation: '+19,9 %',
        preparation: '16 min',
        operating: '94 %',
        stops: '1 h 12 min',
        status: 'Desviación alta',
        color: '#ef4444',
      },
      {
        line: 'Línea C',
        activity: '7 órdenes · 10 tramos',
        expected: 72,
        actual: 76,
        realTime: '29 h 05 min',
        expectedTime: '27 h 30 min',
        deviation: '+5,8 %',
        preparation: '11 min',
        operating: '97 %',
        stops: '38 min',
        status: 'Seguimiento',
        color: '#f59e0b',
      },
      {
        line: 'Línea D',
        activity: '5 órdenes · 8 tramos',
        expected: 64,
        actual: 59,
        realTime: '22 h 40 min',
        expectedTime: '24 h 15 min',
        deviation: '-6,5 %',
        preparation: '6 min',
        operating: '99 %',
        stops: '12 min',
        status: 'Mejora sobre previsión',
        color: '#38bdf8',
      },
      {
        line: 'Línea E',
        activity: '6 órdenes · 7 tramos',
        expected: 70,
        actual: 82,
        realTime: '26 h 50 min',
        expectedTime: '23 h 20 min',
        deviation: '+15,0 %',
        preparation: '14 min',
        operating: '95 %',
        stops: '52 min',
        status: 'Requiere revisión',
        color: '#f97316',
      },
    ];

    const processGroups = [
      {
        process: 'Proceso A',
        lines: '3 líneas',
        activity: '21 órdenes · 30 tramos cerrados',
        standard: '96,8 %',
        standardColor: '#22c55e',
        accent: '#38bdf8',
        production: '84 h 20 min',
        deviation: '+5,4 %',
        preparation: '8 min',
        operating: '98 %',
        stops: '1 h 14 min',
        rows: [
          {
            label: 'Línea A',
            value: 'Desv. +4,8 %',
            progress: 78,
            color: '#22c55e',
          },
          {
            label: 'Línea C',
            value: 'Desv. +5,8 %',
            progress: 76,
            color: '#f59e0b',
          },
          {
            label: 'Línea D',
            value: 'Desv. -6,5 %',
            progress: 59,
            color: '#38bdf8',
          },
        ],
      },
      {
        process: 'Proceso B',
        lines: '2 líneas',
        activity: '12 órdenes · 16 tramos cerrados',
        standard: '89,4 %',
        standardColor: '#fcd34d',
        accent: '#a78bfa',
        production: '55 h 25 min',
        deviation: '+17,2 %',
        preparation: '15 min',
        operating: '94 %',
        stops: '2 h 04 min',
        rows: [
          {
            label: 'Línea B',
            value: 'Desv. +19,9 %',
            progress: 86,
            color: '#ef4444',
          },
          {
            label: 'Línea E',
            value: 'Desv. +15,0 %',
            progress: 82,
            color: '#f97316',
          },
        ],
      },
    ];

    const summaryMetrics = [
      {
        label: 'Producción real / prevista',
        value: '139 h 45 min',
        secondary: '/ 128 h 40 min',
        detail: '+8,6 % sobre la referencia',
        progress: 86,
        color: '#38bdf8',
      },
      {
        label: 'Preparación media',
        value: '11 min',
        secondary: 'por cambio',
        detail: '4,2 % del tiempo operativo',
        progress: 42,
        color: '#fcd34d',
      },
      {
        label: 'Operativo / parado',
        value: '96 %',
        secondary: '/ 4 %',
        detail: '3 h 18 min de parada',
        progress: 96,
        color: '#22c55e',
      },
      {
        label: 'Cobertura estándar',
        value: '94 %',
        secondary: 'calculable',
        detail: '3 órdenes sin estimación',
        progress: 94,
        color: '#a78bfa',
      },
    ];

    return (
      <Stack
        spacing={1.45}
        sx={{
          minHeight: compact
            ? 500
            : {
                lg: 'clamp(500px, calc(100vh - 250px), 620px)',
              },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1.05,
          }}
        >
          {summaryMetrics.map((metric, index) => (
            <motion.div
              key={metric.label}
              initial={{
                opacity: 0,
                y: 14,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.4,
                delay: index * 0.06,
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  px: 1.2,
                  py: 1.05,
                  borderRadius: 2.5,
                  bgcolor: alpha('#0f172a', 0.4),
                  border: `1px solid ${alpha(metric.color, 0.24)}`,
                }}
              >
                <Typography
                  sx={{
                    color: metric.color,
                    fontSize: '0.6rem',
                    fontWeight: 950,
                    letterSpacing: 0.8,
                    textTransform: 'uppercase',
                  }}
                >
                  {metric.label}
                </Typography>

                <Stack
                  direction="row"
                  spacing={0.55}
                  alignItems="baseline"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{
                    mt: 0.42,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontSize: {
                        xs: '1rem',
                        md: '1.18rem',
                      },
                      fontWeight: 950,
                      lineHeight: 1,
                    }}
                  >
                    {metric.value}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: '0.66rem',
                      fontWeight: 850,
                    }}
                  >
                    {metric.secondary}
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    mt: 0.42,
                    color: '#94a3b8',
                    fontSize: '0.61rem',
                    lineHeight: 1.3,
                  }}
                >
                  {metric.detail}
                </Typography>

                <Box
                  sx={{
                    mt: 0.72,
                    height: 4,
                    overflow: 'hidden',
                    borderRadius: 999,
                    bgcolor: alpha('#94a3b8', 0.1),
                  }}
                >
                  <motion.div
                    initial={{
                      width: 0,
                    }}
                    animate={{
                      width: `${metric.progress}%`,
                    }}
                    transition={{
                      duration: 0.8,
                      delay: 0.18 + index * 0.07,
                    }}
                    style={{
                      height: '100%',
                      borderRadius: 999,
                      backgroundColor: metric.color,
                    }}
                  />
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 390,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg: 'minmax(0, 1.35fr) minmax(290px, 0.65fr)',
            },
            gap: 1.35,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              p: {
                xs: 1.35,
                md: 1.55,
              },
              borderRadius: 3,
              bgcolor: alpha('#0f172a', 0.34),
              border: `1px solid ${alpha('#94a3b8', 0.14)}`,
            }}
          >
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              justifyContent="space-between"
              alignItems={{
                xs: 'flex-start',
                sm: 'center',
              }}
              spacing={0.8}
            >
              <Box>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontSize: '0.87rem',
                    fontWeight: 950,
                  }}
                >
                  Rendimiento por línea
                </Typography>

                <Typography
                  sx={{
                    mt: 0.22,
                    color: '#64748b',
                    fontSize: '0.63rem',
                  }}
                >
                  Producción real y prevista, preparación, ocupación y paradas
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={1.1}
                flexWrap="wrap"
                useFlexGap
              >
                {[
                  ['Previsto', '#64748b'],
                  ['Real', '#38bdf8'],
                  ['Desviación', '#f59e0b'],
                ].map(([label, color]) => (
                  <Stack
                    key={label}
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        width: 13,
                        height: 4,
                        borderRadius: 999,
                        bgcolor: color,
                      }}
                    />

                    <Typography
                      sx={{
                        color: '#94a3b8',
                        fontSize: '0.59rem',
                        fontWeight: 800,
                      }}
                    >
                      {label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>

            <Box
              sx={{
                mt: 1.2,
                display: {
                  xs: 'none',
                  md: 'grid',
                },
                gridTemplateColumns:
                  '82px minmax(180px, 1fr) 66px 62px 62px 70px',
                gap: 0.9,
                px: 0.6,
                pb: 0.65,
                borderBottom: `1px solid ${alpha('#94a3b8', 0.1)}`,
              }}
            >
              {[
                'Línea',
                'Producción real / prevista',
                'Desv.',
                'Prep.',
                'Operativo',
                'Paradas',
              ].map((label) => (
                <Typography
                  key={label}
                  sx={{
                    color: '#64748b',
                    fontSize: '0.55rem',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>

            <Stack
              spacing={0}
              sx={{
                mt: 0.35,
              }}
            >
              {performanceLines.map((row, index) => (
                <motion.div
                  key={row.line}
                  initial={{
                    opacity: 0,
                    x: 16,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  transition={{
                    duration: 0.4,
                    delay: 0.08 + index * 0.07,
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: '82px minmax(180px, 1fr) 66px 62px 62px 70px',
                      },
                      gap: {
                        xs: 0.7,
                        md: 0.9,
                      },
                      alignItems: 'center',
                      px: 0.6,
                      py: {
                        xs: 0.85,
                        md: 0.72,
                      },
                      borderBottom:
                        index === performanceLines.length - 1
                          ? 'none'
                          : `1px solid ${alpha('#94a3b8', 0.08)}`,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontSize: '0.7rem',
                          fontWeight: 950,
                        }}
                      >
                        {row.line}
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.12,
                          color: '#64748b',
                          fontSize: '0.54rem',
                          lineHeight: 1.25,
                        }}
                      >
                        {row.activity}
                      </Typography>
                    </Box>

                    <Box>
                      <Stack
                        direction="row"
                        spacing={0.45}
                        alignItems="baseline"
                      >
                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontSize: '0.67rem',
                            fontWeight: 950,
                          }}
                        >
                          {row.realTime}
                        </Typography>

                        <Typography
                          sx={{
                            color: '#64748b',
                            fontSize: '0.54rem',
                            fontWeight: 800,
                          }}
                        >
                          / {row.expectedTime}
                        </Typography>
                      </Stack>

                      <Box
                        sx={{
                          position: 'relative',
                          height: 15,
                          mt: 0.36,
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 1,
                            left: 0,
                            width: `${row.expected}%`,
                            height: 4,
                            borderRadius: 999,
                            bgcolor: alpha('#64748b', 0.55),
                          }}
                        />

                        <motion.div
                          initial={{
                            width: 0,
                          }}
                          animate={{
                            width: `${row.actual}%`,
                          }}
                          transition={{
                            duration: 0.8,
                            delay: 0.2 + index * 0.07,
                          }}
                          style={{
                            position: 'absolute',
                            left: 0,
                            bottom: 1,
                            height: 6,
                            borderRadius: 999,
                            backgroundColor: row.color,
                          }}
                        />
                      </Box>

                      <Typography
                        sx={{
                          color: row.color,
                          fontSize: '0.52rem',
                          fontWeight: 850,
                        }}
                      >
                        {row.status}
                      </Typography>
                    </Box>

                    {[
                      row.deviation,
                      row.preparation,
                      row.operating,
                      row.stops,
                    ].map((value, valueIndex) => (
                      <Typography
                        key={`${row.line}-${valueIndex}`}
                        sx={{
                          color:
                            valueIndex === 0
                              ? row.color
                              : valueIndex === 3 && row.stops !== '12 min'
                                ? '#fca5a5'
                                : '#e2e8f0',
                          fontSize: '0.64rem',
                          fontWeight: 900,
                        }}
                      >
                        {value}
                      </Typography>
                    ))}
                  </Box>
                </motion.div>
              ))}
            </Stack>
          </Box>

          <Stack
            spacing={1.05}
            sx={{
              minWidth: 0,
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontSize: '0.87rem',
                  fontWeight: 950,
                }}
              >
                Rendimiento por proceso
              </Typography>

              <Typography
                sx={{
                  mt: 0.22,
                  color: '#64748b',
                  fontSize: '0.63rem',
                }}
              >
                Lectura agregada de las líneas relacionadas
              </Typography>
            </Box>

            {processGroups.map((group, groupIndex) => (
              <motion.div
                key={group.process}
                initial={{
                  opacity: 0,
                  x: 18,
                }}
                animate={{
                  opacity: 1,
                  x: 0,
                }}
                transition={{
                  duration: 0.42,
                  delay: 0.16 + groupIndex * 0.1,
                }}
                style={{
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    minHeight: 164,
                    p: 1.25,
                    borderRadius: 2.7,
                    bgcolor: alpha(group.accent, 0.07),
                    border: `1px solid ${alpha(group.accent, 0.28)}`,
                    borderLeft: `3px solid ${group.accent}`,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Box>
                      <Stack
                        direction="row"
                        spacing={0.7}
                        alignItems="center"
                      >
                        <Typography
                          sx={{
                            color: group.accent,
                            fontSize: '0.72rem',
                            fontWeight: 950,
                            letterSpacing: 0.7,
                          }}
                        >
                          {group.process}
                        </Typography>

                        <Typography
                          sx={{
                            color: '#94a3b8',
                            fontSize: '0.58rem',
                            fontWeight: 850,
                          }}
                        >
                          {group.lines}
                        </Typography>
                      </Stack>

                      <Typography
                        sx={{
                          mt: 0.25,
                          color: '#64748b',
                          fontSize: '0.56rem',
                        }}
                      >
                        {group.activity}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        px: 0.75,
                        py: 0.34,
                        borderRadius: 999,
                        bgcolor: alpha(group.standardColor, 0.08),
                        border: `1px solid ${alpha(group.standardColor, 0.2)}`,
                      }}
                    >
                      <Typography
                        sx={{
                          color: group.standardColor,
                          fontSize: '0.59rem',
                          fontWeight: 900,
                        }}
                      >
                        Estándar {group.standard}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gap: 0.7,
                      mt: 0.9,
                    }}
                  >
                    {[
                      ['Producción', group.production],
                      ['Desviación', group.deviation],
                      ['Preparación', group.preparation],
                      ['Operativo', group.operating],
                    ].map(([label, value], metricIndex) => (
                      <Box
                        key={`${group.process}-${label}`}
                        sx={{
                          px: 0.75,
                          py: 0.58,
                          borderRadius: 1.7,
                          bgcolor: alpha('#020617', 0.18),
                          border: `1px solid ${alpha('#94a3b8', 0.08)}`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#64748b',
                            fontSize: '0.51rem',
                            fontWeight: 900,
                            textTransform: 'uppercase',
                          }}
                        >
                          {label}
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.15,
                            color:
                              metricIndex === 1
                                ? group.standardColor
                                : '#f8fafc',
                            fontSize: '0.65rem',
                            fontWeight: 950,
                          }}
                        >
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Stack
                    spacing={0.55}
                    sx={{
                      mt: 0.85,
                    }}
                  >
                    {group.rows.map((row, rowIndex) => (
                      <Box key={`${group.process}-${row.label}`}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Typography
                            sx={{
                              color: '#e2e8f0',
                              fontSize: '0.56rem',
                              fontWeight: 900,
                            }}
                          >
                            {row.label}
                          </Typography>

                          <Typography
                            sx={{
                              color: row.color,
                              fontSize: '0.54rem',
                              fontWeight: 850,
                            }}
                          >
                            {row.value}
                          </Typography>
                        </Stack>

                        <Box
                          sx={{
                            mt: 0.25,
                            height: 4,
                            overflow: 'hidden',
                            borderRadius: 999,
                            bgcolor: alpha('#94a3b8', 0.1),
                          }}
                        >
                          <motion.div
                            initial={{
                              width: 0,
                            }}
                            animate={{
                              width: `${row.progress}%`,
                            }}
                            transition={{
                              duration: 0.75,
                              delay:
                                0.32 +
                                groupIndex * 0.12 +
                                rowIndex * 0.06,
                            }}
                            style={{
                              height: '100%',
                              borderRadius: 999,
                              backgroundColor: row.color,
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </motion.div>
            ))}
          </Stack>
        </Box>
      </Stack>
    );
  };

  const scenes = [
    renderCurrentState,
    renderTiming,
    renderAttention,
    renderPerformance,
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: compact
          ? 560
          : {
              lg: 'clamp(640px, calc(100vh - 104px), 780px)',
            },
        overflow: 'hidden',
        borderRadius: {
          xs: 3,
          md: 4,
        },
        bgcolor: alpha('#081120', 0.86),
        border: `1px solid ${alpha('#94a3b8', 0.14)}`,
        boxShadow: `0 34px 90px ${alpha('#020617', 0.34)}`,
        '@keyframes factoryScan': {
          '0%': {
            top: '-12%',
            opacity: 0,
          },
          '18%': {
            opacity: 0.28,
          },
          '82%': {
            opacity: 0.18,
          },
          '100%': {
            top: '112%',
            opacity: 0,
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-24%',
          right: '-15%',
          width: 460,
          height: 460,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(34,197,94,0.08) 0%, transparent 68%)',
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: 90,
          background:
            'linear-gradient(180deg, transparent 0%, rgba(34,197,94,0.035) 48%, transparent 100%)',
          animation: 'factoryScan 7s linear infinite',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          p: {
            xs: 2,
            md: 2.6,
          },
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          justifyContent="space-between"
          alignItems={{
            xs: 'flex-start',
            sm: 'center',
          }}
          spacing={1.3}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <PrecisionManufacturingRoundedIcon
              sx={{
                color: '#22c55e',
                fontSize: 24,
              }}
            />

            <Box>
              <Typography
                sx={{
                  color: '#22c55e',
                  fontSize: '0.62rem',
                  fontWeight: 950,
                  letterSpacing: 1.45,
                }}
              >
                PROAINDUSTRIAL
              </Typography>

              <Typography
                sx={{
                  mt: 0.2,
                  color: '#f8fafc',
                  fontSize: '0.96rem',
                  fontWeight: 950,
                }}
              >
                Control de fábrica
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={0.8}
            alignItems="center"
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: '#22c55e',
                boxShadow: `0 0 15px ${alpha('#22c55e', 0.8)}`,
              }}
            />

            <Typography
              sx={{
                color: '#86efac',
                fontSize: '0.65rem',
                fontWeight: 900,
              }}
            >
              INFORMACIÓN ACTUALIZADA
            </Typography>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={0}
          sx={{
            mt: 1.8,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            borderBottom: `1px solid ${alpha('#94a3b8', 0.12)}`,
          }}
        >
          {tabs.map((tab, index) => {
            const selected = activeStep === index;

            return (
              <Box
                key={tab}
                sx={{
                  position: 'relative',
                  px: {
                    xs: 1.15,
                    md: 1.55,
                  },
                  py: 1,
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    color: selected
                      ? '#f8fafc'
                      : '#64748b',
                    fontSize: '0.68rem',
                    fontWeight: selected
                      ? 900
                      : 800,
                    transition: 'color 0.28s ease',
                  }}
                >
                  {tab}
                </Typography>

                <motion.div
                  animate={{
                    scaleX: selected ? 1 : 0,
                    opacity: selected ? 1 : 0,
                  }}
                  transition={{
                    duration: 0.28,
                  }}
                  style={{
                    position: 'absolute',
                    left: 8,
                    right: 8,
                    bottom: -1,
                    height: 2,
                    borderRadius: 999,
                    backgroundColor: '#22c55e',
                    transformOrigin: 'center',
                  }}
                />
              </Box>
            );
          })}
        </Stack>

        <Box
          sx={{
            minHeight: compact
              ? 430
              : {
                  xs: 430,
                  lg: 'clamp(500px, calc(100vh - 250px), 620px)',
                },
            pt: {
              xs: 2,
              md: 2.5,
            },
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{
                opacity: 0,
                y: 20,
                filter: 'blur(4px)',
              }}
              animate={{
                opacity: 1,
                y: 0,
                filter: 'blur(0px)',
              }}
              exit={{
                opacity: 0,
                y: -16,
                filter: 'blur(3px)',
              }}
              transition={{
                duration: 0.4,
                ease: 'easeOut',
              }}
            >
              {scenes[activeStep]()}
            </motion.div>
          </AnimatePresence>
        </Box>
      </Box>
    </Box>
  );
}

function FactoryControlExperience() {
  const [activeStep, setActiveStep] = useState(0);

  const stories = [
    {
      eyebrow: 'ESTADO ACTUAL',
      title: 'Conocer en tiempo real lo que está pasando en fábrica.',
      text:
        'La operación se visualiza línea a línea para detectar actividad, paradas, retrasos y situaciones que requieren atención inmediata.',
    },
    {
      eyebrow: 'TIEMPOS',
      title: 'Ver cómo se ha ocupado el tiempo en cada línea.',
      text:
        'La actividad se ordena sobre una misma referencia temporal para distinguir fabricación, preparación, esperas y paradas, y compararla con lo previsto.',
    },
    {
      eyebrow: 'ATENCIÓN OPERATIVA',
      title: 'Detectar dónde se pierde tiempo y qué necesita atención.',
      text:
        'Paradas, desviaciones, validaciones y resultados de calidad permanecen conectados con la actividad que los ha generado para priorizar la intervención.',
    },
    {
      eyebrow: 'RENDIMIENTO',
      title: 'Comparar líneas y procesos para entender el rendimiento.',
      text:
        'La actividad acumulada permite contrastar tiempos reales y previstos, identificar desviaciones y localizar dónde se concentran preparación, paradas o pérdida de rendimiento.',
    },
  ];

  return (
    <Box
      id="factory-control"
      sx={{
        position: 'relative',
        bgcolor: '#081120',
        borderTop: `1px solid ${alpha('#94a3b8', 0.07)}`,
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          pt: {
            xs: 11,
            md: 15,
          },
        }}
      >
        <Box sx={{ maxWidth: 830 }}>
          <Typography
            variant="overline"
            sx={{
              color: '#22c55e',
              fontWeight: 950,
              letterSpacing: 2.2,
            }}
          >
            01 · CONTROL DE FÁBRICA
          </Typography>

          <Typography
            component="h2"
            sx={{
              mt: 2,
              maxWidth: 810,
              color: '#f8fafc',
              fontSize: {
                xs: '2.2rem',
                sm: '2.9rem',
                md: '3.7rem',
              },
              fontWeight: 950,
              lineHeight: 1.06,
              letterSpacing: '-0.06em',
            }}
          >
            Saber qué está ocurriendo y dónde necesita atención la operación.
          </Typography>

          <Typography
            sx={{
              mt: 2.8,
              maxWidth: 700,
              color: '#94a3b8',
              fontSize: {
                xs: '0.98rem',
                md: '1.05rem',
              },
              lineHeight: 1.82,
            }}
          >
            Una visión compartida de actividad, estados, tiempos,
            incidencias y rendimiento, adaptada a la estructura
            productiva de cada empresa.
          </Typography>
        </Box>
      </Container>

      <Box
        sx={{
          display: {
            xs: 'none',
            lg: 'block',
          },
          mt: 6,
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns:
                'minmax(280px, 0.6fr) minmax(0, 1.4fr)',
              gap: {
                lg: 7,
                xl: 10,
              },
              alignItems: 'start',
            }}
          >
            <Box
              sx={{
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 7,
                  top: 48,
                  bottom: 48,
                  width: '1px',
                  bgcolor: alpha('#64748b', 0.2),
                }}
              />

              {stories.map((story, index) => {
                const selected = activeStep === index;

                return (
                  <motion.div
                    key={story.eyebrow}
                    onViewportEnter={() => setActiveStep(index)}
                    viewport={{
                      once: false,
                      amount: 0.46,
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        minHeight: 'max(1280px, 190vh)',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'sticky',
                          top: 'clamp(150px, 34vh, 300px)',
                          pl: 5,
                          pr: 1,
                          py: 3,
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            width: 15,
                            height: 15,
                            borderRadius: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: selected
                              ? '#22c55e'
                              : '#081120',
                            border: `2px solid ${selected ? '#22c55e' : alpha('#64748b', 0.48)}`,
                            boxShadow: selected
                              ? `0 0 20px ${alpha('#22c55e', 0.58)}`
                              : 'none',
                            transition:
                              'background-color 0.32s ease, border-color 0.32s ease, box-shadow 0.32s ease',
                          }}
                        />

                        <Box
                          sx={{
                            opacity: selected ? 1 : 0.34,
                            transform: selected
                              ? 'translateX(0)'
                              : 'translateX(-10px)',
                            transition:
                              'opacity 0.36s ease, transform 0.36s ease',
                          }}
                        >
                          <Typography
                            sx={{
                              color: selected
                                ? '#22c55e'
                                : '#64748b',
                              fontSize: '0.65rem',
                              fontWeight: 950,
                              letterSpacing: 1.3,
                            }}
                          >
                            0{index + 1} · {story.eyebrow}
                          </Typography>

                          <Typography
                            component="h3"
                            sx={{
                              mt: 1.7,
                              maxWidth: 400,
                              color: '#f8fafc',
                              fontSize: {
                                lg: '1.8rem',
                                xl: '2.15rem',
                              },
                              fontWeight: 950,
                              lineHeight: 1.12,
                              letterSpacing: '-0.045em',
                            }}
                          >
                            {story.title}
                          </Typography>

                          <Typography
                            sx={{
                              mt: 1.7,
                              maxWidth: 390,
                              color: '#94a3b8',
                              fontSize: '0.94rem',
                              lineHeight: 1.78,
                            }}
                          >
                            {story.text}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                );
              })}
            </Box>

            <Box
              sx={{
                position: 'sticky',
                top: {
                  lg: 88,
                  xl: 96,
                },
                alignSelf: 'start',
                pt: 2,
              }}
            >
              <FactoryControlVisual
                activeStep={activeStep}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <Container
        maxWidth="xl"
        sx={{
          display: {
            xs: 'block',
            lg: 'none',
          },
          py: 8,
        }}
      >
        <Stack spacing={9}>
          {stories.map((story, index) => (
            <motion.div
              key={story.eyebrow}
              initial={{
                opacity: 0,
                y: 28,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: false,
                amount: 0.18,
              }}
              transition={{
                duration: 0.55,
              }}
            >
              <Box
                sx={{
                  pl: 2.2,
                  borderLeft: `2px solid ${alpha('#22c55e', 0.62)}`,
                }}
              >
                <Typography
                  sx={{
                    color: '#22c55e',
                    fontSize: '0.65rem',
                    fontWeight: 950,
                    letterSpacing: 1.3,
                  }}
                >
                  0{index + 1} · {story.eyebrow}
                </Typography>

                <Typography
                  component="h3"
                  sx={{
                    mt: 1.2,
                    color: '#f8fafc',
                    fontSize: '1.65rem',
                    fontWeight: 950,
                    lineHeight: 1.13,
                    letterSpacing: '-0.04em',
                  }}
                >
                  {story.title}
                </Typography>

                <Typography
                  sx={{
                    mt: 1.35,
                    color: '#94a3b8',
                    fontSize: '0.92rem',
                    lineHeight: 1.72,
                  }}
                >
                  {story.text}
                </Typography>
              </Box>

              <Box sx={{ mt: 3 }}>
                <FactoryControlVisual
                  activeStep={index}
                  compact
                />
              </Box>
            </motion.div>
          ))}
        </Stack>
      </Container>

      <Container maxWidth="xl">
        <Box
          sx={{
            pb: {
              xs: 11,
              md: 15,
            },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              maxWidth: 700,
              color: '#cbd5e1',
              fontSize: {
                xs: '0.95rem',
                md: '1.02rem',
              },
              lineHeight: 1.8,
            }}
          >
            Lo que ocurre en planta es el punto de partida. El
            siguiente paso es entender cómo esa actividad se
            traduce en coste.
          </Typography>

          <Box
            sx={{
              mt: 2.5,
              width: '1px',
              height: {
                xs: 60,
                md: 84,
              },
              background: `linear-gradient(180deg, ${alpha('#22c55e', 0.62)} 0%, ${alpha('#38bdf8', 0)} 100%)`,
            }}
          />
        </Box>
      </Container>
    </Box>
  );
}

function IndustrialCostResultsExperience() {
  const [selectedResultIndex, setSelectedResultIndex] = useState(1);

  const costResults = [
    {
      id: 'product-a',
      product: 'Producto A',
      family: 'Familia estándar',
      production: '12.480 uds',
      totalCost: '54.280 €',
      unitCost: '4,35 €/ud',
      status: 'Completo',
      statusColor: '#22c55e',
      dominantLayer: 'Material directo',
      valuationMode: 'PMP calculado',
      laborModel: 'Por etapa',
      layers: [
        {
          label: 'Material directo',
          amount: '31.482 €',
          share: 58,
          color: '#f59e0b',
        },
        {
          label: 'Mano de obra',
          amount: '13.027 €',
          share: 24,
          color: '#22c55e',
        },
        {
          label: 'Indirectos',
          amount: '9.771 €',
          share: 18,
          color: '#a78bfa',
        },
      ],
      stages: [
        {
          label: 'Preparación',
          amount: '6.420 €',
          progress: 34,
        },
        {
          label: 'Producción',
          amount: '37.580 €',
          progress: 88,
        },
        {
          label: 'Calidad y cierre',
          amount: '10.280 €',
          progress: 48,
        },
      ],
      warning: null,
    },
    {
      id: 'product-b',
      product: 'Producto B',
      family: 'Serie industrial',
      production: '8.940 uds',
      totalCost: '47.960 €',
      unitCost: '5,36 €/ud',
      status: 'Con avisos',
      statusColor: '#f59e0b',
      dominantLayer: 'Material directo',
      valuationMode: 'Valoración combinada',
      laborModel: 'Modelo mixto',
      layers: [
        {
          label: 'Material directo',
          amount: '29.256 €',
          share: 61,
          color: '#f59e0b',
        },
        {
          label: 'Mano de obra',
          amount: '10.072 €',
          share: 21,
          color: '#22c55e',
        },
        {
          label: 'Indirectos',
          amount: '8.632 €',
          share: 18,
          color: '#a78bfa',
        },
      ],
      stages: [
        {
          label: 'Preparación',
          amount: '7.180 €',
          progress: 42,
        },
        {
          label: 'Producción',
          amount: '31.940 €',
          progress: 82,
        },
        {
          label: 'Calidad y cierre',
          amount: '8.840 €',
          progress: 51,
        },
      ],
      warning: {
        layer: 'Material directo',
        title: 'Valoración pendiente en una parte del material',
        text:
          'El coste permanece calculado, pero una parte del consumo utiliza una fuente de valoración provisional.',
      },
    },
    {
      id: 'product-c',
      product: 'Producto C',
      family: 'Configuración especial',
      production: '5.760 kg',
      totalCost: '38.420 €',
      unitCost: '6,67 €/kg',
      status: 'Parcial',
      statusColor: '#38bdf8',
      dominantLayer: 'Material directo',
      valuationMode: 'Última entrada disponible',
      laborModel: 'Por producto',
      layers: [
        {
          label: 'Material directo',
          amount: '20.747 €',
          share: 54,
          color: '#f59e0b',
        },
        {
          label: 'Mano de obra',
          amount: '9.989 €',
          share: 26,
          color: '#22c55e',
        },
        {
          label: 'Indirectos',
          amount: '7.684 €',
          share: 20,
          color: '#a78bfa',
        },
      ],
      stages: [
        {
          label: 'Preparación',
          amount: '5.960 €',
          progress: 39,
        },
        {
          label: 'Producción',
          amount: '24.830 €',
          progress: 76,
        },
        {
          label: 'Calidad y cierre',
          amount: '7.630 €',
          progress: 46,
        },
      ],
      warning: {
        layer: 'Indirectos',
        title: 'Reparto indirecto todavía incompleto',
        text:
          'El resultado incorpora las capas disponibles, pero existe una parte del reparto pendiente de consolidar.',
      },
    },
    {
      id: 'product-d',
      product: 'Producto D',
      family: 'Producto configurable',
      production: 'Sin base suficiente',
      totalCost: '—',
      unitCost: '—',
      status: 'Sin coste',
      statusColor: '#ef4444',
      dominantLayer: 'No disponible',
      valuationMode: 'Pendiente',
      laborModel: 'No informado',
      layers: [
        {
          label: 'Material directo',
          amount: '—',
          share: 0,
          color: '#f59e0b',
        },
        {
          label: 'Mano de obra',
          amount: '—',
          share: 0,
          color: '#22c55e',
        },
        {
          label: 'Indirectos',
          amount: '—',
          share: 0,
          color: '#a78bfa',
        },
      ],
      stages: [
        {
          label: 'Preparación',
          amount: '—',
          progress: 0,
        },
        {
          label: 'Producción',
          amount: '—',
          progress: 0,
        },
        {
          label: 'Calidad y cierre',
          amount: '—',
          progress: 0,
        },
      ],
      warning: {
        layer: 'Producción',
        title: 'Falta una base productiva calculable',
        text:
          'El producto permanece visible, pero todavía no existe información suficiente para obtener un coste unitario fiable.',
      },
    },
  ];

  const selectedResult = costResults[selectedResultIndex];

  return (
    <Box
      id="industrial-costs"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#091323',
        borderTop: `1px solid ${alpha('#38bdf8', 0.08)}`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-12%',
          right: '-12%',
          width: {
            xs: 420,
            md: 760,
          },
          height: {
            xs: 420,
            md: 760,
          },
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(56,189,248,0.075) 0%, rgba(56,189,248,0.015) 42%, transparent 72%)',
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          left: '-14%',
          bottom: '-24%',
          width: {
            xs: 360,
            md: 660,
          },
          height: {
            xs: 360,
            md: 660,
          },
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(167,139,250,0.055) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Container
        maxWidth="xl"
        sx={{
          position: 'relative',
          zIndex: 1,
          py: {
            xs: 11,
            md: 15,
          },
        }}
      >
        <motion.div
          initial={{
            opacity: 0,
            y: 28,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.45,
          }}
          transition={{
            duration: 0.58,
            ease: 'easeOut',
          }}
        >
          <Box
            sx={{
              maxWidth: 850,
              mb: {
                xs: 5,
                md: 7,
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: '#38bdf8',
                fontWeight: 950,
                letterSpacing: 2.2,
              }}
            >
              02 · COSTES INDUSTRIALES
            </Typography>

            <Typography
              component="h2"
              sx={{
                mt: 2,
                maxWidth: 820,
                color: '#f8fafc',
                fontSize: {
                  xs: '2.2rem',
                  sm: '2.9rem',
                  md: '3.7rem',
                },
                fontWeight: 950,
                lineHeight: 1.06,
                letterSpacing: '-0.06em',
              }}
            >
              Entender cuánto cuesta producir y qué explica ese resultado.
            </Typography>

            <Typography
              sx={{
                mt: 2.8,
                maxWidth: 760,
                color: '#94a3b8',
                fontSize: {
                  xs: '0.98rem',
                  md: '1.05rem',
                },
                lineHeight: 1.82,
              }}
            >
              Cada producto reúne su coste total y unitario, la
              composición entre materiales, mano de obra e indirectos
              y los avisos que deben tenerse en cuenta para
              interpretar el cálculo.
            </Typography>
          </Box>
        </motion.div>

        <motion.div
          initial={{
            opacity: 0,
            y: 34,
            scale: 0.985,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
            scale: 1,
          }}
          viewport={{
            once: true,
            amount: 0.16,
          }}
          transition={{
            duration: 0.65,
            ease: 'easeOut',
          }}
        >
          <Box
            sx={{
              minHeight: {
                xs: 'auto',
                lg: 720,
              },
              overflow: 'hidden',
              borderRadius: {
                xs: 3,
                md: 4,
              },
              bgcolor: alpha('#081120', 0.9),
              border: `1px solid ${alpha('#94a3b8', 0.15)}`,
              boxShadow: `0 36px 100px ${alpha('#020617', 0.38)}`,
            }}
          >
            <Box
              sx={{
                px: {
                  xs: 2,
                  md: 2.6,
                },
                py: 2.1,
                borderBottom: `1px solid ${alpha('#94a3b8', 0.12)}`,
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                justifyContent: 'space-between',
                alignItems: {
                  xs: 'flex-start',
                  sm: 'center',
                },
                gap: 1.4,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <InsightsRoundedIcon
                  sx={{
                    color: '#38bdf8',
                    fontSize: 24,
                  }}
                />

                <Box>
                  <Typography
                    sx={{
                      color: '#38bdf8',
                      fontSize: '0.62rem',
                      fontWeight: 950,
                      letterSpacing: 1.4,
                    }}
                  >
                    PROAINDUSTRIAL
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.2,
                      color: '#f8fafc',
                      fontSize: '0.96rem',
                      fontWeight: 950,
                    }}
                  >
                    Resultados de costes
                  </Typography>
                </Box>
              </Stack>

              <Stack
                direction="row"
                spacing={0.85}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Box
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 999,
                    bgcolor: alpha('#38bdf8', 0.08),
                    border: `1px solid ${alpha('#38bdf8', 0.2)}`,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#bae6fd',
                      fontSize: '0.65rem',
                      fontWeight: 900,
                    }}
                  >
                    PERIODO ACTUAL
                  </Typography>
                </Box>

                <Stack
                  direction="row"
                  spacing={0.7}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: '#22c55e',
                      boxShadow: `0 0 12px ${alpha('#22c55e', 0.7)}`,
                    }}
                  />

                  <Typography
                    sx={{
                      color: '#86efac',
                      fontSize: '0.64rem',
                      fontWeight: 900,
                    }}
                  >
                    CÁLCULO ACTUALIZADO
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 0,
                borderBottom: `1px solid ${alpha('#94a3b8', 0.1)}`,
              }}
            >
              {[
                {
                  label: 'Coste total visible',
                  value: '184.600 €',
                  detail: 'Cuatro productos en el periodo',
                  color: '#38bdf8',
                },
                {
                  label: 'Coste directo',
                  value: '149.900 €',
                  detail: 'Materiales y mano de obra',
                  color: '#22c55e',
                },
                {
                  label: 'Coste indirecto',
                  value: '34.700 €',
                  detail: '18,8 % del total visible',
                  color: '#a78bfa',
                },
                {
                  label: 'Coste medio unitario',
                  value: '4,82 €',
                  detail: 'Ponderado sobre producción',
                  color: '#f59e0b',
                },
              ].map((metric, index) => (
                <motion.div
                  key={metric.label}
                  initial={{
                    opacity: 0,
                    y: 12,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                    amount: 0.6,
                  }}
                  transition={{
                    duration: 0.38,
                    delay: 0.1 + index * 0.06,
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      px: {
                        xs: 1.5,
                        md: 2,
                      },
                      py: 1.55,
                      borderRight: {
                        xs:
                          index % 2 === 0
                            ? `1px solid ${alpha('#94a3b8', 0.09)}`
                            : 'none',
                        md:
                          index < 3
                            ? `1px solid ${alpha('#94a3b8', 0.09)}`
                            : 'none',
                      },
                      borderBottom: {
                        xs:
                          index < 2
                            ? `1px solid ${alpha('#94a3b8', 0.09)}`
                            : 'none',
                        md: 'none',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.61rem',
                        fontWeight: 900,
                        textTransform: 'uppercase',
                      }}
                    >
                      {metric.label}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.4,
                        color: metric.color,
                        fontSize: {
                          xs: '1.18rem',
                          md: '1.42rem',
                        },
                        fontWeight: 950,
                        lineHeight: 1,
                      }}
                    >
                      {metric.value}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.45,
                        color: '#64748b',
                        fontSize: '0.61rem',
                        lineHeight: 1.35,
                      }}
                    >
                      {metric.detail}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  lg: 'minmax(280px, 0.72fr) minmax(0, 1.28fr)',
                },
                minHeight: {
                  xs: 'auto',
                  lg: 535,
                },
              }}
            >
              <Box
                sx={{
                  p: {
                    xs: 1.6,
                    md: 2,
                  },
                  borderRight: {
                    xs: 'none',
                    lg: `1px solid ${alpha('#94a3b8', 0.1)}`,
                  },
                  borderBottom: {
                    xs: `1px solid ${alpha('#94a3b8', 0.1)}`,
                    lg: 'none',
                  },
                  bgcolor: alpha('#020617', 0.12),
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={1}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontSize: '0.86rem',
                        fontWeight: 950,
                      }}
                    >
                      Coste por producto
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.3,
                        color: '#64748b',
                        fontSize: '0.64rem',
                        lineHeight: 1.4,
                      }}
                    >
                      Selecciona un resultado para revisar su composición
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      px: 0.8,
                      py: 0.4,
                      borderRadius: 999,
                      bgcolor: alpha('#38bdf8', 0.08),
                      border: `1px solid ${alpha('#38bdf8', 0.18)}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#bae6fd',
                        fontSize: '0.6rem',
                        fontWeight: 900,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      3/4 con coste
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  spacing={0.85}
                  sx={{
                    mt: 1.5,
                  }}
                >
                  {costResults.map((result, index) => {
                    const selected = selectedResultIndex === index;

                    return (
                      <motion.div
                        key={result.id}
                        initial={{
                          opacity: 0,
                          x: -14,
                        }}
                        whileInView={{
                          opacity: 1,
                          x: 0,
                        }}
                        viewport={{
                          once: true,
                          amount: 0.5,
                        }}
                        transition={{
                          duration: 0.38,
                          delay: 0.18 + index * 0.07,
                        }}
                      >
                        <Box
                          role="button"
                          tabIndex={0}
                          aria-pressed={selected}
                          onMouseEnter={() =>
                            setSelectedResultIndex(index)
                          }
                          onClick={() =>
                            setSelectedResultIndex(index)
                          }
                          onKeyDown={(event) => {
                            if (
                              event.key === 'Enter' ||
                              event.key === ' '
                            ) {
                              event.preventDefault();
                              setSelectedResultIndex(index);
                            }
                          }}
                          sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            px: 1.25,
                            py: 1.15,
                            borderRadius: 2.5,
                            cursor: 'pointer',
                            outline: 'none',
                            bgcolor: selected
                              ? alpha('#38bdf8', 0.075)
                              : alpha('#0f172a', 0.3),
                            border: `1px solid ${
                              selected
                                ? alpha('#38bdf8', 0.32)
                                : alpha('#94a3b8', 0.1)
                            }`,
                            transform: selected
                              ? 'translateX(4px)'
                              : 'translateX(0)',
                            transition:
                              'background-color 0.25s ease, border-color 0.25s ease, transform 0.25s ease',
                            '&:focus-visible': {
                              borderColor: '#38bdf8',
                              boxShadow: `0 0 0 2px ${alpha('#38bdf8', 0.16)}`,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: selected
                                ? '3px'
                                : '0px',
                              bgcolor: '#38bdf8',
                              transition: 'width 0.24s ease',
                            }}
                          />

                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            spacing={1}
                            alignItems="flex-start"
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  color: '#f8fafc',
                                  fontSize: '0.76rem',
                                  fontWeight: 950,
                                }}
                              >
                                {result.product}
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 0.2,
                                  color: '#64748b',
                                  fontSize: '0.59rem',
                                }}
                              >
                                {result.family}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                px: 0.7,
                                py: 0.32,
                                borderRadius: 999,
                                bgcolor: alpha(result.statusColor, 0.08),
                                border: `1px solid ${alpha(result.statusColor, 0.18)}`,
                                flexShrink: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: result.statusColor,
                                  fontSize: '0.56rem',
                                  fontWeight: 900,
                                }}
                              >
                                {result.status}
                              </Typography>
                            </Box>
                          </Stack>

                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns:
                                'minmax(0, 1fr) auto auto',
                              gap: 1,
                              alignItems: 'end',
                              mt: 0.9,
                            }}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  color: '#64748b',
                                  fontSize: '0.54rem',
                                  fontWeight: 850,
                                }}
                              >
                                PRODUCCIÓN
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 0.15,
                                  color: '#cbd5e1',
                                  fontSize: '0.66rem',
                                  fontWeight: 900,
                                }}
                              >
                                {result.production}
                              </Typography>
                            </Box>

                            <Box sx={{ textAlign: 'right' }}>
                              <Typography
                                sx={{
                                  color: '#64748b',
                                  fontSize: '0.54rem',
                                  fontWeight: 850,
                                }}
                              >
                                TOTAL
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 0.15,
                                  color: '#f8fafc',
                                  fontSize: '0.7rem',
                                  fontWeight: 950,
                                }}
                              >
                                {result.totalCost}
                              </Typography>
                            </Box>

                            <Box sx={{ textAlign: 'right' }}>
                              <Typography
                                sx={{
                                  color: '#64748b',
                                  fontSize: '0.54rem',
                                  fontWeight: 850,
                                }}
                              >
                                UNITARIO
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 0.15,
                                  color: selected
                                    ? '#38bdf8'
                                    : '#cbd5e1',
                                  fontSize: '0.7rem',
                                  fontWeight: 950,
                                }}
                              >
                                {result.unitCost}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </motion.div>
                    );
                  })}
                </Stack>
              </Box>

              <Box
                sx={{
                  minWidth: 0,
                  p: {
                    xs: 1.8,
                    md: 2.2,
                  },
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedResult.id}
                    initial={{
                      opacity: 0,
                      y: 14,
                      filter: 'blur(3px)',
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      filter: 'blur(0px)',
                    }}
                    exit={{
                      opacity: 0,
                      y: -10,
                      filter: 'blur(2px)',
                    }}
                    transition={{
                      duration: 0.34,
                      ease: 'easeOut',
                    }}
                  >
                    <Stack spacing={1.55}>
                      <Stack
                        direction={{
                          xs: 'column',
                          sm: 'row',
                        }}
                        justifyContent="space-between"
                        alignItems={{
                          xs: 'flex-start',
                          sm: 'center',
                        }}
                        spacing={1.2}
                      >
                        <Box>
                          <Typography
                            sx={{
                              color: '#38bdf8',
                              fontSize: '0.62rem',
                              fontWeight: 950,
                              letterSpacing: 1.1,
                            }}
                          >
                            RESULTADO SELECCIONADO
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.4,
                              color: '#f8fafc',
                              fontSize: {
                                xs: '1.15rem',
                                md: '1.35rem',
                              },
                              fontWeight: 950,
                            }}
                          >
                            {selectedResult.product}
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.25,
                              color: '#64748b',
                              fontSize: '0.68rem',
                            }}
                          >
                            {selectedResult.production} producidos
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: alpha(
                              selectedResult.statusColor,
                              0.09,
                            ),
                            border: `1px solid ${alpha(selectedResult.statusColor, 0.22)}`,
                          }}
                        >
                          <Typography
                            sx={{
                              color: selectedResult.statusColor,
                              fontSize: '0.65rem',
                              fontWeight: 900,
                            }}
                          >
                            {selectedResult.status}
                          </Typography>
                        </Box>
                      </Stack>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'repeat(2, minmax(0, 1fr))',
                            md: 'minmax(0, 1.2fr) minmax(0, 0.8fr)',
                          },
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            px: 1.3,
                            py: 1.15,
                            borderRadius: 2.4,
                            bgcolor: alpha('#38bdf8', 0.06),
                            border: `1px solid ${alpha('#38bdf8', 0.18)}`,
                          }}
                        >
                          <Typography
                            sx={{
                              color: '#64748b',
                              fontSize: '0.58rem',
                              fontWeight: 900,
                              textTransform: 'uppercase',
                            }}
                          >
                            Coste total
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.35,
                              color: '#f8fafc',
                              fontSize: {
                                xs: '1.35rem',
                                md: '1.7rem',
                              },
                              fontWeight: 950,
                              lineHeight: 1,
                            }}
                          >
                            {selectedResult.totalCost}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            px: 1.3,
                            py: 1.15,
                            borderRadius: 2.4,
                            bgcolor: alpha('#22c55e', 0.055),
                            border: `1px solid ${alpha('#22c55e', 0.16)}`,
                          }}
                        >
                          <Typography
                            sx={{
                              color: '#64748b',
                              fontSize: '0.58rem',
                              fontWeight: 900,
                              textTransform: 'uppercase',
                            }}
                          >
                            Coste unitario
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.35,
                              color: '#86efac',
                              fontSize: {
                                xs: '1.35rem',
                                md: '1.7rem',
                              },
                              fontWeight: 950,
                              lineHeight: 1,
                            }}
                          >
                            {selectedResult.unitCost}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          p: 1.35,
                          borderRadius: 2.6,
                          bgcolor: alpha('#0f172a', 0.38),
                          border: `1px solid ${alpha('#94a3b8', 0.12)}`,
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={1}
                          alignItems="center"
                        >
                          <Box>
                            <Typography
                              sx={{
                                color: '#f8fafc',
                                fontSize: '0.78rem',
                                fontWeight: 950,
                              }}
                            >
                              Composición del coste
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.2,
                                color: '#64748b',
                                fontSize: '0.6rem',
                              }}
                            >
                              Peso relativo de cada capa
                            </Typography>
                          </Box>

                          <Typography
                            sx={{
                              color: '#fcd34d',
                              fontSize: '0.61rem',
                              fontWeight: 900,
                              textAlign: 'right',
                            }}
                          >
                            Dominante · {selectedResult.dominantLayer}
                          </Typography>
                        </Stack>

                        <Box
                          sx={{
                            display: 'flex',
                            width: '100%',
                            height: 24,
                            overflow: 'hidden',
                            borderRadius: 1.5,
                            mt: 1.05,
                            bgcolor: alpha('#94a3b8', 0.08),
                            border: `1px solid ${alpha('#94a3b8', 0.1)}`,
                          }}
                        >
                          {selectedResult.layers.map((layer, index) => (
                            <motion.div
                              key={`${selectedResult.id}-${layer.label}`}
                              initial={{
                                width: 0,
                                opacity: 0,
                              }}
                              animate={{
                                width: `${layer.share}%`,
                                opacity: layer.share > 0
                                  ? 1
                                  : 0,
                              }}
                              transition={{
                                duration: 0.72,
                                delay: 0.08 + index * 0.12,
                                ease: 'easeOut',
                              }}
                              style={{
                                height: '100%',
                                backgroundColor: layer.color,
                                borderRight:
                                  index < selectedResult.layers.length - 1
                                    ? `1px solid ${alpha('#081120', 0.65)}`
                                    : 'none',
                              }}
                            />
                          ))}
                        </Box>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              sm: 'repeat(3, minmax(0, 1fr))',
                            },
                            gap: 0.9,
                            mt: 1.1,
                          }}
                        >
                          {selectedResult.layers.map((layer, index) => (
                            <motion.div
                              key={`${selectedResult.id}-${layer.label}-detail`}
                              initial={{
                                opacity: 0,
                                y: 10,
                              }}
                              animate={{
                                opacity: 1,
                                y: 0,
                              }}
                              transition={{
                                duration: 0.36,
                                delay: 0.2 + index * 0.08,
                              }}
                            >
                              <Box
                                sx={{
                                  height: '100%',
                                  px: 0.9,
                                  py: 0.75,
                                  borderRadius: 1.8,
                                  bgcolor: alpha(layer.color, 0.055),
                                  border: `1px solid ${alpha(layer.color, 0.14)}`,
                                }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={0.55}
                                  alignItems="center"
                                >
                                  <Box
                                    sx={{
                                      width: 7,
                                      height: 7,
                                      borderRadius: '50%',
                                      bgcolor: layer.color,
                                      flexShrink: 0,
                                    }}
                                  />

                                  <Typography
                                    sx={{
                                      color: '#cbd5e1',
                                      fontSize: '0.59rem',
                                      fontWeight: 850,
                                    }}
                                  >
                                    {layer.label}
                                  </Typography>
                                </Stack>

                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  spacing={0.5}
                                  alignItems="baseline"
                                  sx={{ mt: 0.4 }}
                                >
                                  <Typography
                                    sx={{
                                      color: '#f8fafc',
                                      fontSize: '0.7rem',
                                      fontWeight: 950,
                                    }}
                                  >
                                    {layer.amount}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      color: layer.color,
                                      fontSize: '0.62rem',
                                      fontWeight: 900,
                                    }}
                                  >
                                    {layer.share} %
                                  </Typography>
                                </Stack>
                              </Box>
                            </motion.div>
                          ))}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            md: 'minmax(0, 1.05fr) minmax(220px, 0.95fr)',
                          },
                          gap: 1.1,
                        }}
                      >
                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: 2.5,
                            bgcolor: alpha('#0f172a', 0.34),
                            border: `1px solid ${alpha('#94a3b8', 0.12)}`,
                          }}
                        >
                          <Typography
                            sx={{
                              color: '#f8fafc',
                              fontSize: '0.74rem',
                              fontWeight: 950,
                            }}
                          >
                            Distribución por etapa
                          </Typography>

                          <Stack
                            spacing={0.72}
                            sx={{
                              mt: 0.85,
                            }}
                          >
                            {selectedResult.stages.map((stage, index) => (
                              <Box
                                key={`${selectedResult.id}-${stage.label}`}
                              >
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  spacing={1}
                                >
                                  <Typography
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: '0.59rem',
                                      fontWeight: 850,
                                    }}
                                  >
                                    {stage.label}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      color: '#e2e8f0',
                                      fontSize: '0.61rem',
                                      fontWeight: 900,
                                    }}
                                  >
                                    {stage.amount}
                                  </Typography>
                                </Stack>

                                <Box
                                  sx={{
                                    mt: 0.3,
                                    height: 4,
                                    overflow: 'hidden',
                                    borderRadius: 999,
                                    bgcolor: alpha('#94a3b8', 0.1),
                                  }}
                                >
                                  <motion.div
                                    initial={{
                                      width: 0,
                                    }}
                                    animate={{
                                      width: `${stage.progress}%`,
                                    }}
                                    transition={{
                                      duration: 0.68,
                                      delay: 0.28 + index * 0.08,
                                    }}
                                    style={{
                                      height: '100%',
                                      borderRadius: 999,
                                      backgroundColor: '#38bdf8',
                                    }}
                                  />
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </Box>

                        <Stack spacing={0.85}>
                          <Box
                            sx={{
                              px: 1.15,
                              py: 0.9,
                              borderRadius: 2.2,
                              bgcolor: alpha('#22c55e', 0.045),
                              border: `1px solid ${alpha('#22c55e', 0.13)}`,
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#64748b',
                                fontSize: '0.56rem',
                                fontWeight: 900,
                                textTransform: 'uppercase',
                              }}
                            >
                              Modelo de mano de obra
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.3,
                                color: '#e2e8f0',
                                fontSize: '0.7rem',
                                fontWeight: 950,
                              }}
                            >
                              {selectedResult.laborModel}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              px: 1.15,
                              py: 0.9,
                              borderRadius: 2.2,
                              bgcolor: alpha('#38bdf8', 0.045),
                              border: `1px solid ${alpha('#38bdf8', 0.13)}`,
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#64748b',
                                fontSize: '0.56rem',
                                fontWeight: 900,
                                textTransform: 'uppercase',
                              }}
                            >
                              Modo de valoración
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.3,
                                color: '#e2e8f0',
                                fontSize: '0.7rem',
                                fontWeight: 950,
                              }}
                            >
                              {selectedResult.valuationMode}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>

                      {selectedResult.warning ? (
                        <motion.div
                          initial={{
                            opacity: 0,
                            y: 10,
                          }}
                          animate={{
                            opacity: 1,
                            y: 0,
                          }}
                          transition={{
                            duration: 0.38,
                            delay: 0.24,
                          }}
                        >
                          <Box
                            sx={{
                              px: 1.3,
                              py: 1.05,
                              borderRadius: 2.4,
                              bgcolor:
                                selectedResult.status === 'Sin coste'
                                  ? alpha('#ef4444', 0.075)
                                  : alpha('#f59e0b', 0.075),
                              border: `1px solid ${
                                selectedResult.status === 'Sin coste'
                                  ? alpha('#ef4444', 0.24)
                                  : alpha('#f59e0b', 0.24)
                              }`,
                            }}
                          >
                            <Stack
                              direction={{
                                xs: 'column',
                                sm: 'row',
                              }}
                              spacing={1}
                              justifyContent="space-between"
                              alignItems={{
                                xs: 'flex-start',
                                sm: 'center',
                              }}
                            >
                              <Box>
                                <Typography
                                  sx={{
                                    color:
                                      selectedResult.status === 'Sin coste'
                                        ? '#fca5a5'
                                        : '#fcd34d',
                                    fontSize: '0.61rem',
                                    fontWeight: 950,
                                    letterSpacing: 0.8,
                                  }}
                                >
                                  {selectedResult.warning.layer.toUpperCase()}
                                </Typography>

                                <Typography
                                  sx={{
                                    mt: 0.3,
                                    color: '#f8fafc',
                                    fontSize: '0.72rem',
                                    fontWeight: 950,
                                  }}
                                >
                                  {selectedResult.warning.title}
                                </Typography>

                                <Typography
                                  sx={{
                                    mt: 0.25,
                                    color: '#94a3b8',
                                    fontSize: '0.62rem',
                                    lineHeight: 1.42,
                                  }}
                                >
                                  {selectedResult.warning.text}
                                </Typography>
                              </Box>

                              <Typography
                                sx={{
                                  color: selectedResult.statusColor,
                                  fontSize: '0.62rem',
                                  fontWeight: 900,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                REQUIERE REVISIÓN
                              </Typography>
                            </Stack>
                          </Box>
                        </motion.div>
                      ) : (
                        <motion.div
                          initial={{
                            opacity: 0,
                            y: 10,
                          }}
                          animate={{
                            opacity: 1,
                            y: 0,
                          }}
                          transition={{
                            duration: 0.38,
                            delay: 0.24,
                          }}
                        >
                          <Box
                            sx={{
                              px: 1.3,
                              py: 1.05,
                              borderRadius: 2.4,
                              bgcolor: alpha('#22c55e', 0.065),
                              border: `1px solid ${alpha('#22c55e', 0.2)}`,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.9}
                              alignItems="center"
                            >
                              <CheckCircleRoundedIcon
                                sx={{
                                  color: '#22c55e',
                                  fontSize: 20,
                                }}
                              />

                              <Box>
                                <Typography
                                  sx={{
                                    color: '#bbf7d0',
                                    fontSize: '0.72rem',
                                    fontWeight: 950,
                                  }}
                                >
                                  Cálculo completo
                                </Typography>

                                <Typography
                                  sx={{
                                    mt: 0.2,
                                    color: '#94a3b8',
                                    fontSize: '0.62rem',
                                  }}
                                >
                                  No existen avisos relevantes para la lectura de este resultado.
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </motion.div>
                      )}
                    </Stack>
                  </motion.div>
                </AnimatePresence>
              </Box>
            </Box>
          </Box>
        </motion.div>

        <Box
          sx={{
            pt: {
              xs: 8,
              md: 10,
            },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              maxWidth: 720,
              color: '#cbd5e1',
              fontSize: {
                xs: '0.95rem',
                md: '1.02rem',
              },
              lineHeight: 1.8,
            }}
          >
            El coste explica el impacto económico de la actividad.
            El siguiente paso es entender cómo se organizan y se
            localizan los recursos que hacen posible esa operación.
          </Typography>

          <Box
            sx={{
              mt: 2.5,
              width: '1px',
              height: {
                xs: 60,
                md: 84,
              },
              background: `linear-gradient(180deg, ${alpha('#38bdf8', 0.62)} 0%, ${alpha('#fcd34d', 0)} 100%)`,
            }}
          />
        </Box>
      </Container>
    </Box>
  );
}

type WarehouseLandingCellType =
  | 'empty'
  | 'aisle'
  | 'rack'
  | 'floor'
  | 'blocked';

type WarehouseLandingCell = {
  key: string;
  x: number;
  y: number;
  type: WarehouseLandingCellType;
  label: string;
  assigned: boolean;
  material: string;
  levels: number;
  color: string;
};

function IndustrialInventoryExperience() {
  const [activeInventoryMode, setActiveInventoryMode] = useState<
    'map' | 'count'
  >('map');

  const [selectedMapCellKey, setSelectedMapCellKey] =
    useState('1:1');

  const warehouseCells: WarehouseLandingCell[] = Array.from(
    {
      length: 96,
    },
    (_, index) => {
      const x = index % 12;
      const y = Math.floor(index / 12);

      let type: WarehouseLandingCellType = 'empty';

      if (x === 5 || x === 6 || y === 4) {
        type = 'aisle';
      } else if (
        (x >= 1 && x <= 3 && y >= 1 && y <= 6) ||
        (x >= 8 && x <= 10 && y >= 1 && y <= 3)
      ) {
        type = 'rack';
      } else if (
        x >= 8 &&
        x <= 10 &&
        y >= 5 &&
        y <= 6
      ) {
        type = 'floor';
      } else if (
        (x === 11 && y >= 5) ||
        (x === 0 && y >= 6)
      ) {
        type = 'blocked';
      }

      const isStorage = type === 'rack' || type === 'floor';
      const assigned =
        isStorage && (x + y) % 4 !== 0;

      const storageIndex = y * 12 + x + 1;

      return {
        key: `${x}:${y}`,
        x,
        y,
        type,
        label:
          type === 'rack'
            ? `Estantería ${String(storageIndex).padStart(2, '0')}`
            : type === 'floor'
              ? `Zona ${String(storageIndex).padStart(2, '0')}`
              : '',
        assigned,
        material: assigned
          ? `Material ${String.fromCharCode(
              65 + ((x + y) % 5),
            )}`
          : 'Sin asignación habitual',
        levels:
          type === 'rack'
            ? 3 + ((x + y) % 2)
            : type === 'floor'
              ? 1
              : 0,
        color:
          type === 'rack'
            ? '#38bdf8'
            : type === 'floor'
              ? '#f59e0b'
              : type === 'aisle'
                ? '#22c55e'
                : type === 'blocked'
                  ? '#64748b'
                  : '#334155',
      };
    },
  );

  const selectedMapCell =
    warehouseCells.find(
      (cell) => cell.key === selectedMapCellKey,
    ) ?? null;

  const inventoryRows = [
    {
      material: 'Material A',
      supplier: 'Proveedor habitual',
      unit: 'kg',
      previous: '840',
      current: '826',
      difference: '-14',
      status: 'Completado',
      statusColor: '#22c55e',
      progress: 100,
      note: 'Diferencia dentro de tolerancia',
    },
    {
      material: 'Material B',
      supplier: 'Proveedor habitual',
      unit: 'uds',
      previous: '420',
      current: '438',
      difference: '+18',
      status: 'Sin guardar',
      statusColor: '#f59e0b',
      progress: 82,
      note: 'Cantidad actual modificada',
    },
    {
      material: 'Material C',
      supplier: 'Proveedor alternativo',
      unit: 'kg',
      previous: '1.260',
      current: '—',
      difference: '—',
      status: 'Pendiente',
      statusColor: '#94a3b8',
      progress: 24,
      note: 'Pendiente de recuento físico',
    },
    {
      material: 'Material D',
      supplier: 'Proveedor habitual',
      unit: 'uds',
      previous: '96',
      current: '96',
      difference: '0',
      status: 'Heredado',
      statusColor: '#38bdf8',
      progress: 100,
      note: 'Sin cambios respecto al periodo anterior',
    },
    {
      material: 'Material externo',
      supplier: 'Proveedor externo',
      unit: 'kg',
      previous: '240',
      current: '232',
      difference: '-8',
      status: 'Completado',
      statusColor: '#a78bfa',
      progress: 100,
      note: 'Registro manual incorporado',
    },
  ];

  return (
    <Box
      id="industrial-inventory"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#08111f',
        borderTop: `1px solid ${alpha('#f59e0b', 0.08)}`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-18%',
          left: '-14%',
          width: {
            xs: 420,
            md: 760,
          },
          height: {
            xs: 420,
            md: 760,
          },
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(245,158,11,0.07) 0%, rgba(245,158,11,0.015) 42%, transparent 72%)',
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          right: '-16%',
          bottom: '-28%',
          width: {
            xs: 420,
            md: 720,
          },
          height: {
            xs: 420,
            md: 720,
          },
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(56,189,248,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Container
        maxWidth="xl"
        sx={{
          position: 'relative',
          zIndex: 1,
          py: {
            xs: 11,
            md: 15,
          },
        }}
      >
        <motion.div
          initial={{
            opacity: 0,
            y: 28,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.42,
          }}
          transition={{
            duration: 0.58,
            ease: 'easeOut',
          }}
        >
          <Box
            sx={{
              maxWidth: 880,
              mb: {
                xs: 5,
                md: 7,
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: '#f59e0b',
                fontWeight: 950,
                letterSpacing: 2.2,
              }}
            >
              03 · INVENTARIO Y ALMACÉN
            </Typography>

            <Typography
              component="h2"
              sx={{
                mt: 2,
                maxWidth: 850,
                color: '#f8fafc',
                fontSize: {
                  xs: '2.2rem',
                  sm: '2.9rem',
                  md: '3.7rem',
                },
                fontWeight: 950,
                lineHeight: 1.06,
                letterSpacing: '-0.06em',
              }}
            >
              Organizar el espacio y completar el inventario con una
              referencia común.
            </Typography>

            <Typography
              sx={{
                mt: 2.8,
                maxWidth: 790,
                color: '#94a3b8',
                fontSize: {
                  xs: '0.98rem',
                  md: '1.05rem',
                },
                lineHeight: 1.82,
              }}
            >
              El almacén puede representarse mediante ubicaciones
              operativas y utilizarse como base para ordenar
              materiales, revisar asignaciones y controlar el avance
              de cada recuento.
            </Typography>
          </Box>
        </motion.div>

        <motion.div
          initial={{
            opacity: 0,
            y: 34,
            scale: 0.985,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
            scale: 1,
          }}
          viewport={{
            once: true,
            amount: 0.14,
          }}
          transition={{
            duration: 0.65,
            ease: 'easeOut',
          }}
        >
          <Box
            sx={{
              minHeight: {
                xs: 'auto',
                lg: 760,
              },
              overflow: 'hidden',
              borderRadius: {
                xs: 3,
                md: 4,
              },
              bgcolor: alpha('#081120', 0.92),
              border: `1px solid ${alpha('#94a3b8', 0.15)}`,
              boxShadow: `0 36px 100px ${alpha('#020617', 0.4)}`,
            }}
          >
            <Box
              sx={{
                px: {
                  xs: 2,
                  md: 2.6,
                },
                py: 2,
                borderBottom: `1px solid ${alpha('#94a3b8', 0.12)}`,
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  md: 'row',
                },
                justifyContent: 'space-between',
                alignItems: {
                  xs: 'stretch',
                  md: 'center',
                },
                gap: 1.5,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <PrecisionManufacturingRoundedIcon
                  sx={{
                    color: '#f59e0b',
                    fontSize: 24,
                  }}
                />

                <Box>
                  <Typography
                    sx={{
                      color: '#f59e0b',
                      fontSize: '0.62rem',
                      fontWeight: 950,
                      letterSpacing: 1.4,
                    }}
                  >
                    PROAINDUSTRIAL
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.2,
                      color: '#f8fafc',
                      fontSize: '0.96rem',
                      fontWeight: 950,
                    }}
                  >
                    Inventario y almacén
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  display: 'flex',
                  p: 0.45,
                  borderRadius: 2.2,
                  bgcolor: alpha('#020617', 0.34),
                  border: `1px solid ${alpha('#94a3b8', 0.12)}`,
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                }}
              >
                {[
                  {
                    id: 'map' as const,
                    label: 'Mapa operativo',
                  },
                  {
                    id: 'count' as const,
                    label: 'Recuento',
                  },
                ].map((mode) => {
                  const selected =
                    activeInventoryMode === mode.id;

                  return (
                    <Box
                      key={mode.id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selected}
                      onClick={() =>
                        setActiveInventoryMode(mode.id)
                      }
                      onKeyDown={(event) => {
                        if (
                          event.key === 'Enter' ||
                          event.key === ' '
                        ) {
                          event.preventDefault();
                          setActiveInventoryMode(mode.id);
                        }
                      }}
                      sx={{
                        position: 'relative',
                        px: {
                          xs: 1.2,
                          md: 1.6,
                        },
                        py: 0.75,
                        borderRadius: 1.7,
                        cursor: 'pointer',
                        outline: 'none',
                        flexShrink: 0,
                        color: selected
                          ? '#f8fafc'
                          : '#64748b',
                        bgcolor: selected
                          ? alpha(
                              mode.id === 'map'
                                ? '#38bdf8'
                                : '#f59e0b',
                              0.12,
                            )
                          : 'transparent',
                        border: `1px solid ${
                          selected
                            ? alpha(
                                mode.id === 'map'
                                  ? '#38bdf8'
                                  : '#f59e0b',
                                0.2,
                              )
                            : 'transparent'
                        }`,
                        transition:
                          'background-color 0.24s ease, border-color 0.24s ease, color 0.24s ease',
                        '&:focus-visible': {
                          borderColor:
                            mode.id === 'map'
                              ? '#38bdf8'
                              : '#f59e0b',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.69rem',
                          fontWeight: 900,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {mode.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <AnimatePresence mode="wait">
              {activeInventoryMode === 'map' ? (
                <motion.div
                  key="warehouse-map"
                  initial={{
                    opacity: 0,
                    x: -16,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  exit={{
                    opacity: 0,
                    x: 14,
                  }}
                  transition={{
                    duration: 0.38,
                    ease: 'easeOut',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        lg: 'minmax(0, 1.45fr) minmax(300px, 0.55fr)',
                      },
                      minHeight: {
                        xs: 'auto',
                        lg: 690,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 0,
                        p: {
                          xs: 1.7,
                          md: 2.2,
                        },
                        borderRight: {
                          xs: 'none',
                          lg: `1px solid ${alpha('#94a3b8', 0.1)}`,
                        },
                        borderBottom: {
                          xs: `1px solid ${alpha('#94a3b8', 0.1)}`,
                          lg: 'none',
                        },
                      }}
                    >
                      <Stack
                        direction={{
                          xs: 'column',
                          sm: 'row',
                        }}
                        justifyContent="space-between"
                        alignItems={{
                          xs: 'flex-start',
                          sm: 'center',
                        }}
                        spacing={1.2}
                      >
                        <Box>
                          <Typography
                            sx={{
                              color: '#f8fafc',
                              fontSize: '0.88rem',
                              fontWeight: 950,
                            }}
                          >
                            Plano operativo
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.25,
                              color: '#64748b',
                              fontSize: '0.64rem',
                            }}
                          >
                            Almacén principal · distribución activa
                          </Typography>
                        </Box>

                        <Stack
                          direction="row"
                          spacing={1.1}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {[
                            ['Pasillo', '#22c55e'],
                            ['Estantería', '#38bdf8'],
                            ['Suelo', '#f59e0b'],
                            ['Bloqueado', '#64748b'],
                          ].map(([label, color]) => (
                            <Stack
                              key={label}
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <Box
                                sx={{
                                  width: 9,
                                  height: 9,
                                  borderRadius: 0.7,
                                  bgcolor: color,
                                }}
                              />

                              <Typography
                                sx={{
                                  color: '#94a3b8',
                                  fontSize: '0.59rem',
                                  fontWeight: 850,
                                }}
                              >
                                {label}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Stack>

                      <Box
                        sx={{
                          mt: 1.8,
                          p: {
                            xs: 1.2,
                            md: 1.8,
                          },
                          borderRadius: 3,
                          bgcolor: alpha('#020617', 0.32),
                          border: `1px solid ${alpha('#94a3b8', 0.11)}`,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns:
                              'repeat(12, minmax(0, 1fr))',
                            gap: {
                              xs: 0.45,
                              sm: 0.65,
                              md: 0.8,
                            },
                          }}
                        >
                          {warehouseCells.map(
                            (cell, index) => {
                              const isStorage =
                                cell.type === 'rack' ||
                                cell.type === 'floor';

                              const selected =
                                selectedMapCellKey === cell.key;

                              return (
                                <motion.div
                                  key={cell.key}
                                  initial={{
                                    opacity: 0,
                                    scale: 0.72,
                                  }}
                                  animate={{
                                    opacity:
                                      cell.type === 'empty'
                                        ? 0.42
                                        : 1,
                                    scale: 1,
                                  }}
                                  transition={{
                                    duration: 0.28,
                                    delay:
                                      0.03 +
                                      index * 0.006,
                                  }}
                                >
                                  <Box
                                    role={
                                      isStorage
                                        ? 'button'
                                        : undefined
                                    }
                                    tabIndex={
                                      isStorage
                                        ? 0
                                        : -1
                                    }
                                    aria-pressed={
                                      isStorage
                                        ? selected
                                        : undefined
                                    }
                                    onMouseEnter={() => {
                                      if (isStorage) {
                                        setSelectedMapCellKey(
                                          cell.key,
                                        );
                                      }
                                    }}
                                    onClick={() => {
                                      if (isStorage) {
                                        setSelectedMapCellKey(
                                          cell.key,
                                        );
                                      }
                                    }}
                                    onKeyDown={(event) => {
                                      if (
                                        isStorage &&
                                        (event.key ===
                                          'Enter' ||
                                          event.key === ' ')
                                      ) {
                                        event.preventDefault();
                                        setSelectedMapCellKey(
                                          cell.key,
                                        );
                                      }
                                    }}
                                    sx={{
                                      position: 'relative',
                                      aspectRatio: '1 / 1',
                                      minHeight: {
                                        xs: 20,
                                        sm: 28,
                                        md: 34,
                                      },
                                      borderRadius: {
                                        xs: 0.7,
                                        md: 1,
                                      },
                                      cursor: isStorage
                                        ? 'pointer'
                                        : 'default',
                                      outline: 'none',
                                      overflow: 'hidden',
                                      bgcolor:
                                        cell.type === 'empty'
                                          ? alpha(
                                              '#ffffff',
                                              0.018,
                                            )
                                          : alpha(
                                              cell.color,
                                              selected
                                                ? 0.72
                                                : cell.type ===
                                                    'aisle'
                                                  ? 0.42
                                                  : 0.52,
                                            ),
                                      border: `1px solid ${
                                        selected
                                          ? alpha(
                                              '#a78bfa',
                                              0.95,
                                            )
                                          : cell.type ===
                                              'empty'
                                            ? alpha(
                                                '#94a3b8',
                                                0.07,
                                              )
                                            : alpha(
                                                cell.color,
                                                0.38,
                                              )
                                      }`,
                                      boxShadow: selected
                                        ? `0 0 0 2px ${alpha('#a78bfa', 0.22)}, 0 0 18px ${alpha('#a78bfa', 0.32)}`
                                        : 'none',
                                      transform: selected
                                        ? 'scale(1.08)'
                                        : 'scale(1)',
                                      transition:
                                        'background-color 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease',
                                      '&:focus-visible': {
                                        borderColor:
                                          '#a78bfa',
                                      },
                                    }}
                                  >
                                    {cell.type === 'rack' ? (
                                      <Box
                                        sx={{
                                          position:
                                            'absolute',
                                          inset: '22% 18%',
                                          display:
                                            'grid',
                                          gridTemplateRows:
                                            `repeat(${cell.levels}, minmax(0, 1fr))`,
                                          gap: '2px',
                                        }}
                                      >
                                        {Array.from({
                                          length:
                                            cell.levels,
                                        }).map(
                                          (_, levelIndex) => (
                                            <Box
                                              key={`${cell.key}-${levelIndex}`}
                                              sx={{
                                                borderRadius:
                                                  999,
                                                bgcolor: alpha(
                                                  '#f8fafc',
                                                  0.58,
                                                ),
                                              }}
                                            />
                                          ),
                                        )}
                                      </Box>
                                    ) : null}

                                    {cell.type === 'floor' ? (
                                      <Box
                                        sx={{
                                          position:
                                            'absolute',
                                          inset: '26%',
                                          borderRadius:
                                            0.8,
                                          border: `1px dashed ${alpha('#fef3c7', 0.72)}`,
                                        }}
                                      />
                                    ) : null}

                                    {cell.type ===
                                    'blocked' ? (
                                      <>
                                        <Box
                                          sx={{
                                            position:
                                              'absolute',
                                            left: '18%',
                                            right: '18%',
                                            top: '48%',
                                            height: '1px',
                                            bgcolor:
                                              '#cbd5e1',
                                            transform:
                                              'rotate(45deg)',
                                          }}
                                        />

                                        <Box
                                          sx={{
                                            position:
                                              'absolute',
                                            left: '18%',
                                            right: '18%',
                                            top: '48%',
                                            height: '1px',
                                            bgcolor:
                                              '#cbd5e1',
                                            transform:
                                              'rotate(-45deg)',
                                          }}
                                        />
                                      </>
                                    ) : null}

                                    {isStorage &&
                                    cell.assigned ? (
                                      <Box
                                        sx={{
                                          position:
                                            'absolute',
                                          top: 3,
                                          right: 3,
                                          width: {
                                            xs: 4,
                                            md: 6,
                                          },
                                          height: {
                                            xs: 4,
                                            md: 6,
                                          },
                                          borderRadius:
                                            '50%',
                                          bgcolor:
                                            '#22c55e',
                                          boxShadow: `0 0 8px ${alpha('#22c55e', 0.8)}`,
                                        }}
                                      />
                                    ) : null}
                                  </Box>
                                </motion.div>
                              );
                            },
                          )}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'repeat(2, minmax(0, 1fr))',
                            md: 'repeat(4, minmax(0, 1fr))',
                          },
                          gap: 1,
                          mt: 1.35,
                        }}
                      >
                        {[
                          {
                            label: 'Ubicaciones',
                            value: '42',
                            detail: 'Operativas',
                            color: '#38bdf8',
                          },
                          {
                            label: 'Asignadas',
                            value: '35',
                            detail: 'Con material habitual',
                            color: '#22c55e',
                          },
                          {
                            label: 'Disponibles',
                            value: '7',
                            detail: 'Sin asignación',
                            color: '#f59e0b',
                          },
                          {
                            label: 'Zonas',
                            value: '4',
                            detail: 'Áreas configuradas',
                            color: '#a78bfa',
                          },
                        ].map((metric, index) => (
                          <motion.div
                            key={metric.label}
                            initial={{
                              opacity: 0,
                              y: 10,
                            }}
                            animate={{
                              opacity: 1,
                              y: 0,
                            }}
                            transition={{
                              duration: 0.34,
                              delay:
                                0.42 +
                                index * 0.07,
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                px: 1.1,
                                py: 0.9,
                                borderRadius: 2,
                                bgcolor: alpha(
                                  metric.color,
                                  0.045,
                                ),
                                border: `1px solid ${alpha(metric.color, 0.14)}`,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: metric.color,
                                  fontSize: '0.57rem',
                                  fontWeight: 950,
                                  textTransform:
                                    'uppercase',
                                }}
                              >
                                {metric.label}
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 0.3,
                                  color: '#f8fafc',
                                  fontSize: '1.15rem',
                                  fontWeight: 950,
                                  lineHeight: 1,
                                }}
                              >
                                {metric.value}
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 0.3,
                                  color: '#64748b',
                                  fontSize: '0.56rem',
                                }}
                              >
                                {metric.detail}
                              </Typography>
                            </Box>
                          </motion.div>
                        ))}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: {
                          xs: 1.7,
                          md: 2.1,
                        },
                        bgcolor: alpha('#020617', 0.12),
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontSize: '0.86rem',
                          fontWeight: 950,
                        }}
                      >
                        Ubicación seleccionada
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.25,
                          color: '#64748b',
                          fontSize: '0.63rem',
                        }}
                      >
                        Información operativa de la posición
                      </Typography>

                      <AnimatePresence mode="wait">
                        {selectedMapCell ? (
                          <motion.div
                            key={selectedMapCell.key}
                            initial={{
                              opacity: 0,
                              y: 12,
                            }}
                            animate={{
                              opacity: 1,
                              y: 0,
                            }}
                            exit={{
                              opacity: 0,
                              y: -8,
                            }}
                            transition={{
                              duration: 0.3,
                            }}
                          >
                            <Box
                              sx={{
                                mt: 1.5,
                                p: 1.45,
                                borderRadius: 2.7,
                                bgcolor: alpha(
                                  selectedMapCell.color,
                                  0.075,
                                ),
                                border: `1px solid ${alpha(selectedMapCell.color, 0.23)}`,
                              }}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                spacing={1}
                                alignItems="flex-start"
                              >
                                <Box>
                                  <Typography
                                    sx={{
                                      color:
                                        selectedMapCell.color,
                                      fontSize: '0.61rem',
                                      fontWeight: 950,
                                      letterSpacing: 0.8,
                                    }}
                                  >
                                    {selectedMapCell.type ===
                                    'rack'
                                      ? 'ESTANTERÍA'
                                      : 'ALMACENAMIENTO EN SUELO'}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      mt: 0.45,
                                      color: '#f8fafc',
                                      fontSize: '1rem',
                                      fontWeight: 950,
                                    }}
                                  >
                                    {selectedMapCell.label}
                                  </Typography>
                                </Box>

                                <Box
                                  sx={{
                                    px: 0.8,
                                    py: 0.38,
                                    borderRadius: 999,
                                    bgcolor: alpha(
                                      selectedMapCell.assigned
                                        ? '#22c55e'
                                        : '#f59e0b',
                                      0.09,
                                    ),
                                    border: `1px solid ${alpha(
                                      selectedMapCell.assigned
                                        ? '#22c55e'
                                        : '#f59e0b',
                                      0.2,
                                    )}`,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color:
                                        selectedMapCell.assigned
                                          ? '#86efac'
                                          : '#fcd34d',
                                      fontSize: '0.58rem',
                                      fontWeight: 900,
                                    }}
                                  >
                                    {selectedMapCell.assigned
                                      ? 'Asignada'
                                      : 'Disponible'}
                                  </Typography>
                                </Box>
                              </Stack>

                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns:
                                    'repeat(2, minmax(0, 1fr))',
                                  gap: 0.85,
                                  mt: 1.35,
                                }}
                              >
                                {[
                                  [
                                    'Coordenada',
                                    `${selectedMapCell.x + 1} · ${selectedMapCell.y + 1}`,
                                  ],
                                  [
                                    'Niveles',
                                    String(
                                      selectedMapCell.levels,
                                    ),
                                  ],
                                  [
                                    'Material',
                                    selectedMapCell.material,
                                  ],
                                  [
                                    'Capacidad',
                                    selectedMapCell.assigned
                                      ? 'En uso'
                                      : 'Disponible',
                                  ],
                                ].map(([label, value]) => (
                                  <Box
                                    key={label}
                                    sx={{
                                      px: 0.85,
                                      py: 0.72,
                                      borderRadius: 1.8,
                                      bgcolor: alpha(
                                        '#020617',
                                        0.2,
                                      ),
                                      border: `1px solid ${alpha('#94a3b8', 0.08)}`,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        color: '#64748b',
                                        fontSize:
                                          '0.53rem',
                                        fontWeight: 900,
                                        textTransform:
                                          'uppercase',
                                      }}
                                    >
                                      {label}
                                    </Typography>

                                    <Typography
                                      sx={{
                                        mt: 0.22,
                                        color: '#e2e8f0',
                                        fontSize:
                                          '0.67rem',
                                        fontWeight: 900,
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      {value}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                mt: 1.2,
                                p: 1.25,
                                borderRadius: 2.5,
                                bgcolor: alpha(
                                  '#a78bfa',
                                  0.055,
                                ),
                                border: `1px solid ${alpha('#a78bfa', 0.16)}`,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: '#c4b5fd',
                                  fontSize: '0.6rem',
                                  fontWeight: 950,
                                  letterSpacing: 0.7,
                                }}
                              >
                                ASIGNACIÓN HABITUAL
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 0.45,
                                  color: '#f8fafc',
                                  fontSize: '0.75rem',
                                  fontWeight: 950,
                                }}
                              >
                                {selectedMapCell.material}
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 0.3,
                                  color: '#94a3b8',
                                  fontSize: '0.62rem',
                                  lineHeight: 1.45,
                                }}
                              >
                                La ubicación conserva una referencia
                                operativa del material que suele
                                almacenarse en esta posición.
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                mt: 1.2,
                                p: 1.25,
                                borderRadius: 2.5,
                                bgcolor: alpha(
                                  '#38bdf8',
                                  0.045,
                                ),
                                border: `1px solid ${alpha('#38bdf8', 0.14)}`,
                              }}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                spacing={1}
                              >
                                <Typography
                                  sx={{
                                    color: '#bae6fd',
                                    fontSize: '0.64rem',
                                    fontWeight: 900,
                                  }}
                                >
                                  Ocupación estimada
                                </Typography>

                                <Typography
                                  sx={{
                                    color: '#f8fafc',
                                    fontSize: '0.68rem',
                                    fontWeight: 950,
                                  }}
                                >
                                  {selectedMapCell.assigned
                                    ? '78 %'
                                    : '0 %'}
                                </Typography>
                              </Stack>

                              <Box
                                sx={{
                                  mt: 0.75,
                                  height: 5,
                                  overflow: 'hidden',
                                  borderRadius: 999,
                                  bgcolor: alpha(
                                    '#94a3b8',
                                    0.1,
                                  ),
                                }}
                              >
                                <motion.div
                                  key={`${selectedMapCell.key}-capacity`}
                                  initial={{
                                    width: 0,
                                  }}
                                  animate={{
                                    width:
                                      selectedMapCell.assigned
                                        ? '78%'
                                        : '0%',
                                  }}
                                  transition={{
                                    duration: 0.75,
                                  }}
                                  style={{
                                    height: '100%',
                                    borderRadius: 999,
                                    backgroundColor:
                                      '#38bdf8',
                                  }}
                                />
                              </Box>
                            </Box>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </Box>
                  </Box>
                </motion.div>
              ) : (
                <motion.div
                  key="inventory-count"
                  initial={{
                    opacity: 0,
                    x: 16,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  exit={{
                    opacity: 0,
                    x: -14,
                  }}
                  transition={{
                    duration: 0.38,
                    ease: 'easeOut',
                  }}
                >
                  <Box
                    sx={{
                      minHeight: {
                        xs: 'auto',
                        lg: 690,
                      },
                      p: {
                        xs: 1.7,
                        md: 2.2,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          lg: 'minmax(0, 1.35fr) minmax(280px, 0.65fr)',
                        },
                        gap: 1.4,
                        alignItems: 'stretch',
                      }}
                    >
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2.8,
                          bgcolor: alpha('#0f172a', 0.34),
                          border: `1px solid ${alpha('#94a3b8', 0.13)}`,
                        }}
                      >
                        <Stack
                          direction={{
                            xs: 'column',
                            sm: 'row',
                          }}
                          justifyContent="space-between"
                          alignItems={{
                            xs: 'flex-start',
                            sm: 'center',
                          }}
                          spacing={1.2}
                        >
                          <Box>
                            <Typography
                              sx={{
                                color: '#f8fafc',
                                fontSize: '0.88rem',
                                fontWeight: 950,
                              }}
                            >
                              Recuento del periodo
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.25,
                                color: '#64748b',
                                fontSize: '0.64rem',
                              }}
                            >
                              Comparación entre la referencia anterior
                              y la cantidad observada
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              px: 0.9,
                              py: 0.45,
                              borderRadius: 999,
                              bgcolor: alpha(
                                '#f59e0b',
                                0.08,
                              ),
                              border: `1px solid ${alpha('#f59e0b', 0.2)}`,
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#fcd34d',
                                fontSize: '0.62rem',
                                fontWeight: 900,
                              }}
                            >
                              PERIODO ACTUAL
                            </Typography>
                          </Box>
                        </Stack>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: 'repeat(2, minmax(0, 1fr))',
                              md: 'repeat(4, minmax(0, 1fr))',
                            },
                            gap: 0.9,
                            mt: 1.4,
                          }}
                        >
                          {[
                            {
                              label: 'Líneas',
                              value: '24',
                              color: '#38bdf8',
                            },
                            {
                              label: 'Completadas',
                              value: '16',
                              color: '#22c55e',
                            },
                            {
                              label: 'Pendientes',
                              value: '6',
                              color: '#94a3b8',
                            },
                            {
                              label: 'Sin guardar',
                              value: '2',
                              color: '#f59e0b',
                            },
                          ].map((metric, index) => (
                            <motion.div
                              key={metric.label}
                              initial={{
                                opacity: 0,
                                y: 10,
                              }}
                              animate={{
                                opacity: 1,
                                y: 0,
                              }}
                              transition={{
                                duration: 0.34,
                                delay:
                                  0.04 +
                                  index * 0.06,
                              }}
                            >
                              <Box
                                sx={{
                                  height: '100%',
                                  px: 1,
                                  py: 0.85,
                                  borderRadius: 2,
                                  bgcolor: alpha(
                                    metric.color,
                                    0.045,
                                  ),
                                  border: `1px solid ${alpha(metric.color, 0.14)}`,
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: metric.color,
                                    fontSize: '0.56rem',
                                    fontWeight: 950,
                                    textTransform:
                                      'uppercase',
                                  }}
                                >
                                  {metric.label}
                                </Typography>

                                <Typography
                                  sx={{
                                    mt: 0.32,
                                    color: '#f8fafc',
                                    fontSize: '1.18rem',
                                    fontWeight: 950,
                                    lineHeight: 1,
                                  }}
                                >
                                  {metric.value}
                                </Typography>
                              </Box>
                            </motion.div>
                          ))}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2.8,
                          bgcolor: alpha(
                            '#f59e0b',
                            0.055,
                          ),
                          border: `1px solid ${alpha('#f59e0b', 0.17)}`,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={1}
                          alignItems="baseline"
                        >
                          <Box>
                            <Typography
                              sx={{
                                color: '#fcd34d',
                                fontSize: '0.61rem',
                                fontWeight: 950,
                                letterSpacing: 0.8,
                              }}
                            >
                              AVANCE DEL RECUENTO
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.45,
                                color: '#f8fafc',
                                fontSize: '1.55rem',
                                fontWeight: 950,
                                lineHeight: 1,
                              }}
                            >
                              68 %
                            </Typography>
                          </Box>

                          <CheckCircleRoundedIcon
                            sx={{
                              color: '#22c55e',
                              fontSize: 30,
                            }}
                          />
                        </Stack>

                        <Box
                          sx={{
                            mt: 1.2,
                            height: 8,
                            overflow: 'hidden',
                            borderRadius: 999,
                            bgcolor: alpha(
                              '#94a3b8',
                              0.11,
                            ),
                          }}
                        >
                          <motion.div
                            initial={{
                              width: 0,
                            }}
                            animate={{
                              width: '68%',
                            }}
                            transition={{
                              duration: 1,
                              delay: 0.12,
                              ease: 'easeOut',
                            }}
                            style={{
                              height: '100%',
                              borderRadius: 999,
                              background:
                                'linear-gradient(90deg, #f59e0b 0%, #22c55e 100%)',
                            }}
                          />
                        </Box>

                        <Typography
                          sx={{
                            mt: 0.75,
                            color: '#94a3b8',
                            fontSize: '0.62rem',
                            lineHeight: 1.4,
                          }}
                        >
                          El avance se actualiza al completar y guardar
                          las líneas del periodo.
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        mt: 1.4,
                        overflow: 'hidden',
                        borderRadius: 3,
                        bgcolor: alpha('#0f172a', 0.32),
                        border: `1px solid ${alpha('#94a3b8', 0.13)}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: {
                            xs: 'none',
                            md: 'grid',
                          },
                          gridTemplateColumns:
                            'minmax(160px, 1.25fr) 88px 88px 66px minmax(120px, 0.8fr) 94px',
                          gap: 1,
                          px: 1.4,
                          py: 0.85,
                          bgcolor: alpha(
                            '#020617',
                            0.2,
                          ),
                          borderBottom: `1px solid ${alpha('#94a3b8', 0.1)}`,
                        }}
                      >
                        {[
                          'Material',
                          'Anterior',
                          'Actual',
                          'Diferencia',
                          'Observación',
                          'Estado',
                        ].map((label) => (
                          <Typography
                            key={label}
                            sx={{
                              color: '#64748b',
                              fontSize: '0.54rem',
                              fontWeight: 900,
                              textTransform:
                                'uppercase',
                            }}
                          >
                            {label}
                          </Typography>
                        ))}
                      </Box>

                      <Stack spacing={0}>
                        {inventoryRows.map(
                          (row, index) => (
                            <motion.div
                              key={row.material}
                              initial={{
                                opacity: 0,
                                x: 14,
                              }}
                              animate={{
                                opacity: 1,
                                x: 0,
                              }}
                              transition={{
                                duration: 0.4,
                                delay:
                                  0.12 +
                                  index * 0.08,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: {
                                    xs: '1fr',
                                    md: 'minmax(160px, 1.25fr) 88px 88px 66px minmax(120px, 0.8fr) 94px',
                                  },
                                  gap: {
                                    xs: 0.8,
                                    md: 1,
                                  },
                                  alignItems: 'center',
                                  px: 1.4,
                                  py: {
                                    xs: 1.1,
                                    md: 0.9,
                                  },
                                  bgcolor:
                                    row.status ===
                                    'Sin guardar'
                                      ? alpha(
                                          '#f59e0b',
                                          0.035,
                                        )
                                      : 'transparent',
                                  borderBottom:
                                    index ===
                                    inventoryRows.length -
                                      1
                                      ? 'none'
                                      : `1px solid ${alpha('#94a3b8', 0.08)}`,
                                }}
                              >
                                <Box>
                                  <Typography
                                    sx={{
                                      color: '#f8fafc',
                                      fontSize: '0.7rem',
                                      fontWeight: 950,
                                    }}
                                  >
                                    {row.material}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      mt: 0.16,
                                      color: '#64748b',
                                      fontSize: '0.56rem',
                                    }}
                                  >
                                    {row.supplier} ·{' '}
                                    {row.unit}
                                  </Typography>
                                </Box>

                                {[
                                  row.previous,
                                  row.current,
                                  row.difference,
                                ].map(
                                  (
                                    value,
                                    valueIndex,
                                  ) => (
                                    <Box
                                      key={`${row.material}-${valueIndex}`}
                                    >
                                      <Typography
                                        sx={{
                                          display: {
                                            xs: 'block',
                                            md: 'none',
                                          },
                                          color:
                                            '#64748b',
                                          fontSize:
                                            '0.52rem',
                                          fontWeight: 900,
                                          textTransform:
                                            'uppercase',
                                        }}
                                      >
                                        {valueIndex === 0
                                          ? 'Anterior'
                                          : valueIndex ===
                                              1
                                            ? 'Actual'
                                            : 'Diferencia'}
                                      </Typography>

                                      <Typography
                                        sx={{
                                          mt: {
                                            xs: 0.12,
                                            md: 0,
                                          },
                                          color:
                                            valueIndex ===
                                              2 &&
                                            value !== '0' &&
                                            value !== '—'
                                              ? row.statusColor
                                              : '#e2e8f0',
                                          fontSize:
                                            '0.68rem',
                                          fontWeight: 950,
                                        }}
                                      >
                                        {value}
                                      </Typography>
                                    </Box>
                                  ),
                                )}

                                <Typography
                                  sx={{
                                    color: '#94a3b8',
                                    fontSize: '0.59rem',
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {row.note}
                                </Typography>

                                <Box
                                  sx={{
                                    justifySelf: {
                                      xs: 'start',
                                      md: 'end',
                                    },
                                    px: 0.75,
                                    py: 0.36,
                                    borderRadius: 999,
                                    bgcolor: alpha(
                                      row.statusColor,
                                      0.08,
                                    ),
                                    border: `1px solid ${alpha(row.statusColor, 0.18)}`,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color:
                                        row.statusColor,
                                      fontSize: '0.56rem',
                                      fontWeight: 900,
                                      whiteSpace:
                                        'nowrap',
                                    }}
                                  >
                                    {row.status}
                                  </Typography>
                                </Box>

                                <Box
                                  sx={{
                                    gridColumn: {
                                      xs: '1',
                                      md: '1 / -1',
                                    },
                                    height: 3,
                                    overflow: 'hidden',
                                    borderRadius: 999,
                                    bgcolor: alpha(
                                      '#94a3b8',
                                      0.08,
                                    ),
                                  }}
                                >
                                  <motion.div
                                    initial={{
                                      width: 0,
                                    }}
                                    animate={{
                                      width: `${row.progress}%`,
                                    }}
                                    transition={{
                                      duration: 0.72,
                                      delay:
                                        0.2 +
                                        index *
                                          0.08,
                                    }}
                                    style={{
                                      height: '100%',
                                      borderRadius:
                                        999,
                                      backgroundColor:
                                        row.statusColor,
                                    }}
                                  />
                                </Box>
                              </Box>
                            </motion.div>
                          ),
                        )}
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: {
                          xs: 'column',
                          sm: 'row',
                        },
                        justifyContent: 'space-between',
                        alignItems: {
                          xs: 'stretch',
                          sm: 'center',
                        },
                        gap: 1.1,
                        mt: 1.4,
                        px: 1.4,
                        py: 1.15,
                        borderRadius: 2.7,
                        bgcolor: alpha('#020617', 0.2),
                        border: `1px solid ${alpha('#94a3b8', 0.11)}`,
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: '0.63rem',
                          lineHeight: 1.45,
                        }}
                      >
                        El recuento admite registros de catálogo,
                        materiales externos y cantidades heredadas del
                        periodo anterior.
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={0.8}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {[
                          {
                            label: 'Importar',
                            color: '#38bdf8',
                          },
                          {
                            label: 'Exportar',
                            color: '#a78bfa',
                          },
                          {
                            label: 'Guardar cambios',
                            color: '#22c55e',
                          },
                        ].map((action) => (
                          <Box
                            key={action.label}
                            sx={{
                              px: 1,
                              py: 0.55,
                              borderRadius: 1.8,
                              bgcolor: alpha(
                                action.color,
                                0.07,
                              ),
                              border: `1px solid ${alpha(action.color, 0.17)}`,
                            }}
                          >
                            <Typography
                              sx={{
                                color: action.color,
                                fontSize: '0.6rem',
                                fontWeight: 900,
                              }}
                            >
                              {action.label}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </motion.div>

        <Box
          sx={{
            pt: {
              xs: 8,
              md: 10,
            },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              maxWidth: 720,
              color: '#cbd5e1',
              fontSize: {
                xs: '0.95rem',
                md: '1.02rem',
              },
              lineHeight: 1.8,
            }}
          >
            Un almacén organizado facilita el recuento y mantiene una
            referencia común sobre los recursos disponibles para la
            operación.
          </Typography>

          <Box
            sx={{
              mt: 2.5,
              width: '1px',
              height: {
                xs: 60,
                md: 84,
              },
              background: `linear-gradient(180deg, ${alpha('#f59e0b', 0.62)} 0%, ${alpha('#38bdf8', 0)} 100%)`,
            }}
          />
        </Box>
      </Container>
    </Box>
  );
}

const IndustrialLandingPage: React.FC = () => {
  const navigate = useNavigate();

  const modules = [
    {
      label: 'Control de fábrica',
      value: '3 líneas',
      detail: '2 produciendo · 1 incidencia',
      color: '#22c55e',
      icon: PrecisionManufacturingRoundedIcon,
      progress: 78,
    },
    {
      label: 'Costes industriales',
      value: '4,82 €',
      detail: 'Coste estimado por unidad',
      color: '#38bdf8',
      icon: InsightsRoundedIcon,
      progress: 62,
    },
    {
      label: 'Inventario y almacén',
      value: '24 ubicaciones',
      detail: '18 asignadas · 6 disponibles',
      color: '#fcd34d',
      icon: Inventory2RoundedIcon,
      progress: 74,
    },
    {
      label: 'Tareas y procesos',
      value: '4 etapas',
      detail: 'Producción en curso',
      color: '#a78bfa',
      icon: RouteRoundedIcon,
      progress: 56,
    },
  ];

  const scrollToOperation = () => {
    document
      .getElementById('industrial-operation')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'clip',
        bgcolor: '#081120',
      }}
    >
      <Box
        id="top"
        sx={{
          position: 'relative',
          minHeight: {
            xs: 'auto',
            md: 'calc(100vh - 72px)',
          },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          background:
            'radial-gradient(ellipse at 50% 31%, rgba(34,197,94,0.095) 0%, rgba(34,197,94,0.026) 24%, rgba(15,23,42,0) 56%), linear-gradient(180deg, #0f172a 0%, #0c1629 17%, #081120 50%, #081120 100%)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.1,
            backgroundImage:
              'linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage:
              'linear-gradient(to bottom, rgba(0,0,0,0.9), rgba(0,0,0,0.16), transparent)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: '-18%',
            right: '-10%',
            width: {
              xs: 420,
              md: 760,
            },
            height: {
              xs: 420,
              md: 760,
            },
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(34,197,94,0.09) 0%, rgba(34,197,94,0.018) 42%, transparent 72%)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            left: '-14%',
            bottom: '-34%',
            width: {
              xs: 380,
              md: 680,
            },
            height: {
              xs: 380,
              md: 680,
            },
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(56,189,248,0.065) 0%, rgba(56,189,248,0.012) 44%, transparent 72%)',
            pointerEvents: 'none',
          }}
        />

        <Container
          maxWidth="xl"
          sx={{
            position: 'relative',
            zIndex: 1,
            py: {
              xs: 8,
              md: 10,
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              minHeight: {
                xs: 680,
                md: 760,
              },
              pt: {
                xs: 5,
                md: 7,
              },
              pb: {
                xs: 3,
                md: 5,
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                left: '50%',
                top: {
                  xs: '27%',
                  md: '31%',
                },
                transform: 'translate(-50%, -50%)',
                width: '100%',
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'center',
                  mt: {
                    xs: -0.1,
                    md: -0.35,
                  },
                  color: alpha('#dcfce7', 0.038),
                  fontSize: {
                    xs: '5.8rem',
                    sm: '8.5rem',
                    md: '12.5rem',
                    lg: '15.5rem',
                    xl: '18rem',
                  },
                  fontWeight: 950,
                  letterSpacing: '-0.085em',
                  lineHeight: 0.78,
                  whiteSpace: 'nowrap',
                  mx: 'auto',
                }}
              >
                INDUSTRIAL
              </Typography>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: {
                  xs: '27%',
                  md: '31%',
                },
                transform: 'translate(-50%, -50%)',
                width: {
                  xs: 310,
                  md: 530,
                },
                height: {
                  xs: 190,
                  md: 270,
                },
                background:
                  'radial-gradient(ellipse at center, rgba(34,197,94,0.11) 0%, rgba(34,197,94,0.028) 28%, transparent 70%)',
                filter: 'blur(18px)',
                pointerEvents: 'none',
              }}
            />

            <Stack
              alignItems="center"
              spacing={0}
              sx={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                maxWidth: 1040,
                mx: 'auto',
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="PROAIndustrial"
                sx={{
                  height: {
                    xs: 130,
                    sm: 170,
                    md: 225,
                  },
                  width: 'auto',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 0 20px rgba(34,197,94,0.2))',
                }}
              />

              <Box
                sx={{
                  mt: {
                    xs: 15,
                    md: 22,
                  },
                  maxWidth: 820,
                  mx: 'auto',
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: '#22c55e',
                    fontSize: {
                      xs: '0.68rem',
                      md: '0.76rem',
                    },
                    fontWeight: 950,
                    letterSpacing: 2.8,
                  }}
                >
                  PROAIndustrial
                </Typography>

                <Typography
                  component="h1"
                  sx={{
                    mt: 1.7,
                    maxWidth: 790,
                    mx: 'auto',
                    color: '#f8fafc',
                    fontWeight: 950,
                    fontSize: {
                      xs: '2.25rem',
                      sm: '2.8rem',
                      md: '3.5rem',
                    },
                    lineHeight: 1.06,
                    letterSpacing: '-0.058em',
                  }}
                >
                  Más claridad sobre lo que ocurre cada día en fábrica.
                </Typography>

                <Typography
                  sx={{
                    mt: 2.4,
                    maxWidth: 700,
                    mx: 'auto',
                    color: '#94a3b8',
                    fontSize: {
                      xs: '0.96rem',
                      md: '1.06rem',
                    },
                    lineHeight: 1.8,
                  }}
                >
                  Producción, costes, almacén y procesos internos conectados en un entorno
                  adaptado a la forma real de trabajar de cada empresa.
                </Typography>

                <Stack
                  direction={{
                    xs: 'column',
                    sm: 'row',
                  }}
                  justifyContent="center"
                  spacing={1.6}
                  sx={{
                    mt: 3.5,
                  }}
                >
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={scrollToOperation}
                    sx={{
                      bgcolor: '#22c55e',
                      color: '#020617',
                      fontWeight: 900,
                      textTransform: 'none',
                      px: 3.2,
                      py: 1.25,
                      boxShadow: `0 14px 36px ${alpha('#22c55e', 0.15)}`,
                      '&:hover': {
                        bgcolor: '#16a34a',
                        boxShadow: `0 18px 42px ${alpha('#22c55e', 0.2)}`,
                      },
                    }}
                  >
                    Explorar la operación
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => navigate('/proa')}
                    sx={{
                      borderColor: alpha('#94a3b8', 0.26),
                      color: '#cbd5e1',
                      fontWeight: 850,
                      textTransform: 'none',
                      px: 3.2,
                      py: 1.25,
                      '&:hover': {
                        borderColor: alpha('#cbd5e1', 0.62),
                        bgcolor: alpha('#94a3b8', 0.05),
                      },
                    }}
                  >
                    Volver a PROA
                  </Button>
                </Stack>
              </Box>

              <Box
                sx={{
                  width: '100%',
                  maxWidth: 920,
                  mt: {
                    xs: 6,
                    md: 7,
                  },
                  pt: 2.6,
                  borderTop: `1px solid ${alpha('#94a3b8', 0.11)}`,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(4, minmax(0, 1fr))',
                  },
                  columnGap: {
                    xs: 1.5,
                    md: 3,
                  },
                  rowGap: 2,
                }}
              >
                {modules.map((module) => {
                  const ModuleIcon = module.icon;

                  return (
                    <Stack
                      key={module.label}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent={{
                        xs: 'flex-start',
                        md: 'center',
                      }}
                      sx={{
                        minWidth: 0,
                      }}
                    >
                      <ModuleIcon
                        sx={{
                          color: module.color,
                          fontSize: 19,
                          flexShrink: 0,
                        }}
                      />

                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: {
                            xs: '0.72rem',
                            sm: '0.78rem',
                          },
                          fontWeight: 850,
                          lineHeight: 1.3,
                          textAlign: 'left',
                        }}
                      >
                        {module.label}
                      </Typography>
                    </Stack>
                  );
                })}
              </Box>
            </Stack>
          </Box>
        </Container>
      </Box>

      <Box
        id="industrial-operation"
        sx={{
          scrollMarginTop: {
            xs: 72,
            md: 88,
          },
          position: 'relative',
          py: {
            xs: 10,
            md: 15,
          },
          bgcolor: '#0b1424',
          borderTop: `1px solid ${alpha('#94a3b8', 0.07)}`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '4%',
            left: '50%',
            width: {
              xs: 420,
              md: 860,
            },
            height: {
              xs: 420,
              md: 860,
            },
            transform: 'translateX(-50%)',
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(34,197,94,0.045) 0%, transparent 68%)',
            pointerEvents: 'none',
          }}
        />

        <Container
          maxWidth="xl"
          sx={{
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              maxWidth: 780,
              mx: 'auto',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: '#22c55e',
                fontWeight: 900,
                letterSpacing: 2.2,
              }}
            >
              UNA OPERACIÓN CONECTADA
            </Typography>

            <Typography
              component="h2"
              sx={{
                mt: 2,
                color: '#f8fafc',
                fontWeight: 950,
                fontSize: {
                  xs: '2.15rem',
                  sm: '2.8rem',
                  md: '3.45rem',
                },
                lineHeight: 1.08,
                letterSpacing: '-0.055em',
              }}
            >
              Cuatro ámbitos que forman parte de una misma operación.
            </Typography>

            <Typography
              sx={{
                mt: 3,
                maxWidth: 700,
                mx: 'auto',
                color: '#94a3b8',
                fontSize: {
                  xs: '0.98rem',
                  md: '1.06rem',
                },
                lineHeight: 1.85,
              }}
            >
              El recorrido conecta lo que ocurre en planta, su impacto económico, la
              organización de los recursos y el seguimiento del trabajo interno.
            </Typography>
          </Box>

          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 1240,
              mx: 'auto',
              mt: {
                xs: 7,
                md: 10,
              },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: {
                  xs: 14,
                  md: '50%',
                },
                width: '1px',
                transform: {
                  xs: 'none',
                  md: 'translateX(-50%)',
                },
                background: `linear-gradient(
                  180deg,
                  ${alpha('#22c55e', 0)} 0%,
                  ${alpha('#22c55e', 0.28)} 8%,
                  ${alpha('#38bdf8', 0.24)} 36%,
                  ${alpha('#fcd34d', 0.22)} 65%,
                  ${alpha('#a78bfa', 0.24)} 88%,
                  ${alpha('#a78bfa', 0)} 100%
                )`,
                pointerEvents: 'none',
              }}
            />

            {modules.map((module, index) => {
              const ModuleIcon = module.icon;

              const descriptions = [
                'Visibilidad sobre actividad, estados, tiempos e incidencias para entender qué ocurre y qué necesita atención.',
                'Una lectura del coste industrial por capas para saber cuánto cuesta producir y qué factores explican el resultado.',
                'Una representación clara de espacios, ubicaciones y disponibilidad para conectar materiales y operación.',
                'Procesos organizados por etapas, prioridades y fechas para seguir el trabajo y anticipar desviaciones.',
              ];

              const narrativeLabels = [
                'Entender la actividad',
                'Traducirla a coste',
                'Organizar los recursos',
                'Coordinar lo que viene después',
              ];

              const textOnLeft = index % 2 === 0;

              return (
                <Box
                  key={module.label}
                  sx={{
                    position: 'relative',
                    py: {
                      xs: 7,
                      md: 10,
                    },
                    pl: {
                      xs: 5,
                      md: 0,
                    },
                    borderBottom:
                      index < modules.length - 1
                        ? `1px solid ${alpha('#94a3b8', 0.07)}`
                        : 'none',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: {
                        xs: 62,
                        md: '50%',
                      },
                      left: {
                        xs: 0,
                        md: '50%',
                      },
                      width: 29,
                      height: 29,
                      transform: {
                        xs: 'none',
                        md: 'translate(-50%, -50%)',
                      },
                      borderRadius: '50%',
                      bgcolor: '#0b1424',
                      border: `1px solid ${alpha(module.color, 0.48)}`,
                      boxShadow: `0 0 0 8px ${alpha('#0b1424', 0.96)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: module.color,
                        boxShadow: `0 0 16px ${alpha(module.color, 0.7)}`,
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'minmax(0, 0.86fr) minmax(0, 1.14fr)',
                      },
                      columnGap: {
                        md: 12,
                        lg: 15,
                      },
                      rowGap: 4,
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        order: {
                          xs: 1,
                          md: textOnLeft ? 1 : 2,
                        },
                        maxWidth: 450,
                        ml: {
                          xs: 0,
                          md: textOnLeft ? 0 : 'auto',
                        },
                        textAlign: {
                          xs: 'left',
                          md: textOnLeft ? 'right' : 'left',
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent={{
                          xs: 'flex-start',
                          md: textOnLeft ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <ModuleIcon
                          sx={{
                            color: module.color,
                            fontSize: 23,
                          }}
                        />

                        <Typography
                          sx={{
                            color: module.color,
                            fontSize: '0.68rem',
                            fontWeight: 950,
                            letterSpacing: 1.45,
                          }}
                        >
                          0{index + 1}
                        </Typography>
                      </Stack>

                      <Typography
                        component="h3"
                        sx={{
                          mt: 1.4,
                          color: '#f8fafc',
                          fontWeight: 950,
                          fontSize: {
                            xs: '1.65rem',
                            md: '2.15rem',
                          },
                          lineHeight: 1.12,
                          letterSpacing: '-0.045em',
                        }}
                      >
                        {module.label}
                      </Typography>

                      <Typography
                        sx={{
                          mt: 1.7,
                          color: '#94a3b8',
                          fontSize: {
                            xs: '0.92rem',
                            md: '0.98rem',
                          },
                          lineHeight: 1.78,
                        }}
                      >
                        {descriptions[index]}
                      </Typography>

                      <Typography
                        sx={{
                          mt: 1.6,
                          color: '#cbd5e1',
                          fontSize: '0.76rem',
                          fontWeight: 900,
                          letterSpacing: 0.35,
                          textTransform: 'uppercase',
                        }}
                      >
                        {narrativeLabels[index]}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        order: {
                          xs: 2,
                          md: textOnLeft ? 2 : 1,
                        },
                        minWidth: 0,
                      }}
                    >
                      <IndustrialModuleGraphic
                        moduleIndex={index}
                        color={module.color}
                      />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Container>
      </Box>

      <FactoryControlExperience />

      <IndustrialCostResultsExperience />

      <IndustrialInventoryExperience />

      <Box
        id="industrial-contact"
        sx={{
          scrollMarginTop: 90,
          py: {
            xs: 10,
            md: 14,
          },
          bgcolor: '#0f172a',
          borderTop: `1px solid ${alpha('#94a3b8', 0.08)}`,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="overline"
              sx={{
                color: '#22c55e',
                fontWeight: 900,
                letterSpacing: 2,
              }}
            >
              CONVERSACIÓN INICIAL
            </Typography>

            <Typography
              variant="h3"
              sx={{
                mt: 2,
                color: '#f8fafc',
                fontWeight: 950,
                letterSpacing: '-0.05em',
              }}
            >
              La implantación empieza entendiendo cómo funciona hoy tu fábrica.
            </Typography>

            <Typography
              sx={{
                mt: 2.5,
                mx: 'auto',
                maxWidth: 720,
                color: '#94a3b8',
                lineHeight: 1.8,
              }}
            >
              En las siguientes iteraciones construiremos aquí el cierre comercial y el canal de
              contacto específico de PROAIndustrial.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default IndustrialLandingPage;